import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, memo, useId, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  BarPlot,
  LinePlot,
  ChartDataProvider,
  ChartsWrapper,
  ChartsSurface,
  ChartsAxis,
  ChartsLegend,
  ChartsTooltip,
  ChartsGrid,
  ChartsClipPath,
  ChartsAxisHighlight,
  DEFAULT_X_AXIS_KEY,
  DEFAULT_Y_AXIS_KEY
} from '@mui/x-charts';
import styles from './ForecastPage.module.css';
import ForecastProgressFooter from './ForecastProgressFooter';
import dashStyles from '../../Dashboard.module.css';
import BackButton from '../../shared/BackButton';
import loadingStyles from '../../shared/Loading.module.css';
import { MEET_EXPERT_PATH, SEE_ALL_MONEY_MOVES_PATH } from '../your-quick-richuels/constants';
import financialCheckupIcon from '../../../assets/financial-checkup-cta-icon.png';
import cashFlowIncomeIcon from '../../../assets/cashflow-income-icon.png';
import cashFlowExpensesIcon from '../../../assets/cashflow-expenses-icon.png';
import cashFlowSavingsIcon from '../../../assets/cashflow-savings-icon.png';
import config, { logger } from '../../../config';
import {
  getToken,
  hideOnboardingConfetti,
  getApiHeaders,
} from '../../../utils/api';

import {
  getFinancialDashboardData,
  getCareerShiftRunwayData,
  formatCurrency,
  formatCompactCurrencyTwoDecimals,
  normalizeOnboardingProgressKeys,
  parseDurationToMonths,
  normalizeLongevityPercent,
} from '../../../utils/onboardingUtils';
import { buildTransitionIncomeCashFlow } from '../../../utils/transitionIncomeCashFlow';
import {
  FINANCIAL_CONSTANTS,
  calculateAgeFromBirthYear,
  isMarried,
  extractAssetsAndDebts,
  parseNumericValue,
  getSocialSecurityMonthlyBenefit,
  HEALTHCARE_COSTS,
} from '../../../utils/helper';
import { getTopPercentLocal, getTopPercentUS } from '../../../utils/netWorthPercentiles';
import { getContentReflectionOpenState, subscribeContentReflectionOpenState } from '../../../utils/contentReflectionBridge';
import { useContentCatalog } from '../../../utils/contentCatalog';
import ContentParts from '../../shared/ContentParts';
import { BREAKPOINT_SMALL_MOBILE, BREAKPOINT_TABLET } from './constants';
import {
  ACTION_CARD_DESCRIPTION_PLACEHOLDER_KEYS,
  actionCardDescPlaceholderKeyToSnake,
} from './actionCards';
import {
  CFP_LONGEVITY_BENCHMARK_PCT,
  formatLongevityBand,
  formatLongevityCardScore,
  formatLongevitySuccessScoreForDisplay,
  formatRunwayMonthsDisplay,
  normalizeCheckupAndActionPlan,
} from './formatters';

export {
  CALENDLY_FINANCE_SYNC_URL,
  ENABLE_SCHEDULE_CFP_SESSION_PAGE,
  FORECAST_ACTION_CARD_KIND,
  FORECAST_ACTION_CARD_ORDER,
  openCfpScheduling,
} from './actionCards';

/* Responsive breakpoints (px) — shared by walk-through overlay and charts */


/* ---------------------------------------------------------------------------
   Walk Me Through — guided tour step definitions and overlay component
   --------------------------------------------------------------------------- */

/** Anchors that must come from GET /api/content. The tour stays hidden until all of these exist. */
const WMT_REQUIRED_ANCHORS = [
  'checkup_summary',
  'monthly_cash_flow',
  'short_term_runway',
  'long_term_success',
  'lifetime_income',
  'projected_net_worth',
];

/**
 * Page order. Section refs in FinancialDashboard must follow this same sequence.
 * transition_cash_flow and key_insights are filled locally until the content API publishes them.
 */
const WMT_STEP_ORDER = [
  'checkup_summary',
  'monthly_cash_flow',
  'short_term_runway',
  'transition_cash_flow',
  'long_term_success',
  'key_insights',
  'lifetime_income',
  'projected_net_worth',
];

const WMT_CHART_ANCHORS = new Set([
  'transition_cash_flow',
  'lifetime_income',
  'projected_net_worth',
]);

const WMT_LOCAL_STEPS = {
  transition_cash_flow: {
    anchor: 'transition_cash_flow',
    title: 'TRANSITION CASH FLOW',
    read_more: false,
    summary: [
      { text: 'This is your Transition Income Cash Flow Snapshot,', emphasis: true },
      { text: ' showing projected income and expenses month by month so you can see where cash flow dips during the career shift.' },
    ],
  },
  key_insights: {
    anchor: 'key_insights',
    title: 'KEY INSIGHTS',
    read_more: false,
    summary: [
      { text: 'Key Insights turns your runway and cash flow into three takeaways:', emphasis: true },
      { text: ' how long your savings last, where income dips, and what a little extra income could change.' },
    ],
  },
};

/** Filled from GET /api/content during render so tour layout helpers can read the same copy. */
let walkthroughSteps = [];

function orderedWalkthroughSteps(steps) {
  const byAnchor = new Map((Array.isArray(steps) ? steps : []).map((step) => [step.anchor, step]));
  const apiReady = WMT_REQUIRED_ANCHORS.every((anchor) => byAnchor.has(anchor));
  if (!apiReady) return [];
  return WMT_STEP_ORDER.map((anchor) => byAnchor.get(anchor) || WMT_LOCAL_STEPS[anchor]);
}

function wmtAnchorAt(stepIndex) {
  return walkthroughSteps[stepIndex]?.anchor || '';
}

function WalkthroughDetails({ items }) {
  if (!Array.isArray(items) || !items.length) return null;
  return (
    <ul className={styles.wmtDescList}>
      {items.map((item, index) => (
        <li key={index}><ContentParts parts={item} /></li>
      ))}
    </ul>
  );
}

function getWmtStepDescriptionContent(step) {
  if (!step) return { summary: null, full: null };
  return {
    summary: <ContentParts parts={step.summary} />,
    full: step.read_more ? <WalkthroughDetails items={step.details} /> : null,
  };
}

function wmtStepHasReadMore(step) {
  return Boolean(step?.read_more);
}

const WMT_SECTION_PADDING = 10;
const WMT_VIEWPORT_MARGIN = 16;
const WMT_TOOLTIP_GAP = 12;
/** Arrow height — keep clear of the viewport edge so it is not clipped. */
const WMT_ARROW_SIZE = 10;
/* Extra lift for docked tooltip on mobile so it sits clearly above the bottom edge. */
const WMT_MOBILE_DOCK_BOTTOM = 100;
const WMT_PHONE_DOCK_BOTTOM = 120;
/** Minimum usable tooltip height when viewport space is tight. */
const WMT_TOOLTIP_MIN_HEIGHT = 160;
/** Forecast cards stack into one column at this width. */
const WMT_STACKED_BREAKPOINT = 1024;

function isWmtChartStep(stepIndex) {
  return WMT_CHART_ANCHORS.has(wmtAnchorAt(stepIndex));
}

function isWmtMobileLayout() {
  return getWmtViewportMetrics().isTablet;
}

function isWmtCompactLayout() {
  return getWmtViewportMetrics().isCompact;
}

function getWmtPassThroughStyle() {
  if (!isWmtMobileLayout()) return null;
  return {
    pointerEvents: 'none',
    touchAction: 'pan-y',
  };
}

function createWmtHiddenSpotlightRect() {
  return {
    top: 0,
    left: 0,
    width: 0,
    height: 0,
    bottom: 0,
    right: 0,
    spotlightVisible: false,
  };
}

function isWmtSpotlightVisible(rect) {
  return Boolean(rect?.spotlightVisible && rect.width > 0 && rect.height > 0);
}

/** Above tour covers (8910) so chart hovers stay visible during chart tour steps. */
const DASHBOARD_CHARTS_TOOLTIP_Z_INDEX = 8950;
const WMT_SCROLL_SETTLE_MS = 72;
const WMT_STEP_CHANGE_MS = 720;
const WMT_TOOLTIP_OUT_MS = 200;
const WMT_TOOLTIP_IN_MS = 280;
const WMT_MORPH_MS = 520;

function getWmtScrollBlock() {
  // Every forecast section is a vertical block. Align the top so the spotlight
  // stays under the sticky header and above a docked tooltip.
  return 'start';
}

function getWmtStickyHeaderOffset() {
  if (typeof document === 'undefined') return 0;
  const header = document.querySelector('header[role="banner"]');
  if (!header) return 0;
  const position = window.getComputedStyle(header).position;
  if (position !== 'sticky' && position !== 'fixed') return 0;
  return Math.ceil(header.getBoundingClientRect().height);
}

function getScrollableAncestors(element) {
  const ancestors = [];
  let parent = element?.parentElement;
  while (parent) {
    const { overflowY, overflowX } = window.getComputedStyle(parent);
    const isScrollable =
      /(auto|scroll|overlay)/.test(`${overflowY} ${overflowX}`);
    if (
      isScrollable
      && (parent.scrollHeight > parent.clientHeight || parent.scrollWidth > parent.clientWidth)
    ) {
      ancestors.push(parent);
    }
    parent = parent.parentElement;
  }
  return ancestors;
}

function getWmtViewportMetrics() {
  const layoutVw = window.innerWidth;
  const layoutVh = window.innerHeight;
  const visualViewport = window.visualViewport;
  const isTablet = layoutVw <= BREAKPOINT_TABLET;

  return {
    vw: isTablet && visualViewport ? visualViewport.width : layoutVw,
    vh: isTablet && visualViewport ? visualViewport.height : layoutVh,
    isTablet,
    isCompact: layoutVw <= WMT_STACKED_BREAKPOINT,
    isSmallMobile: layoutVw <= BREAKPOINT_SMALL_MOBILE,
  };
}

/** Layout-viewport pixels below the visible visual viewport (mobile browser chrome). */
function getWmtVisualViewportBottomInset() {
  const vv = window.visualViewport;
  if (!vv) return 0;
  return Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop));
}

/** Read CSS env(safe-area-inset-bottom); cached per layout width. */
let wmtSafeAreaBottomCache = { vw: -1, value: 0 };
function getWmtSafeAreaInsetBottom() {
  if (typeof document === 'undefined') return 0;
  const vw = window.innerWidth;
  if (wmtSafeAreaBottomCache.vw === vw) return wmtSafeAreaBottomCache.value;

  const probe = document.createElement('div');
  probe.setAttribute('aria-hidden', 'true');
  probe.style.cssText = [
    'position:fixed',
    'bottom:0',
    'left:0',
    'width:0',
    'height:env(safe-area-inset-bottom, 0px)',
    'visibility:hidden',
    'pointer-events:none',
  ].join(';');
  document.body.appendChild(probe);
  const value = probe.getBoundingClientRect().height || 0;
  probe.remove();
  wmtSafeAreaBottomCache = { vw, value };
  return value;
}

/**
 * Bottom offset for docked tour tooltip.
 * Phone: sit higher in the viewport (above browser chrome / home indicator).
 * Wider screens: clear the viewport edge so the card is not clipped.
 * Short viewports (landscape phones) cap the offset so the spotlight stays visible.
 */
function getWmtDockedBottomOffset(stepIndex) {
  const { vh, isSmallMobile } = getWmtViewportMetrics();
  const base = isWmtMobileLayout()
    ? (isSmallMobile ? WMT_PHONE_DOCK_BOTTOM : WMT_MOBILE_DOCK_BOTTOM)
      + getWmtVisualViewportBottomInset()
      + getWmtSafeAreaInsetBottom()
    : WMT_VIEWPORT_MARGIN + 8;
  const tooltipHeight = estimateWmtTooltipHeight(stepIndex, false);
  const minSpotlight = Math.min(168, Math.round(vh * 0.34));
  const maxOffset = Math.max(
    WMT_VIEWPORT_MARGIN,
    vh - tooltipHeight - WMT_TOOLTIP_GAP - minSpotlight,
  );
  return Math.min(base, maxOffset);
}

function getWmtSectionPadding(stepIndex) {
  if (!isWmtMobileLayout()) {
    return WMT_SECTION_PADDING;
  }

  const { isSmallMobile } = getWmtViewportMetrics();
  if (isSmallMobile) return 2;
  if (wmtAnchorAt(stepIndex) === 'checkup_summary') return 4;
  if (isWmtChartStep(stepIndex)) return 3;
  return 4;
}

function estimateWmtTooltipHeight(stepIndex, descExpanded = false) {
  const { vw } = getWmtViewportMetrics();
  const hasBackButton = stepIndex > 0;
  let height;

  if (vw <= BREAKPOINT_SMALL_MOBILE) {
    height = hasBackButton ? 228 : 208;
  } else if (vw <= BREAKPOINT_TABLET) {
    height = hasBackButton ? 212 : 196;
  } else {
    height = hasBackButton ? 204 : 188;
  }

  if (descExpanded && wmtStepHasReadMore(walkthroughSteps[stepIndex])) {
    height += 168;
  }

  return height;
}

function getWmtTooltipWidth(vw) {
  if (vw <= BREAKPOINT_SMALL_MOBILE) {
    return vw - 24;
  }
  if (vw <= BREAKPOINT_TABLET) {
    return Math.min(340, vw - 32);
  }
  return Math.min(360, vw - 32);
}

function shouldDockWmtTooltip(stepIndex, sectionHeight = 0) {
  // Runway now wraps the transition chart, so keep the tooltip docked and the
  // spotlight on the headline instead of covering that chart.
  if (
    isWmtCompactLayout()
    || isWmtChartStep(stepIndex)
    || wmtAnchorAt(stepIndex) === 'short_term_runway'
  ) return true;
  if (!(sectionHeight > 0)) return false;
  const { vh } = getWmtViewportMetrics();
  const floatingBlock = estimateWmtTooltipHeight(stepIndex, false)
    + WMT_TOOLTIP_GAP
    + WMT_ARROW_SIZE
    + WMT_SECTION_PADDING;
  return sectionHeight + floatingBlock > vh - WMT_VIEWPORT_MARGIN;
}

function getUnclippedSectionHeight(sectionEl) {
  const bounds = sectionEl ? getWmtRawSectionBounds(sectionEl) : null;
  if (!bounds) return 0;
  return Math.max(0, bounds.bottom - bounds.top);
}

function getWmtBottomReserve(stepIndex, sectionHeight = 0) {
  if (!shouldDockWmtTooltip(stepIndex, sectionHeight)) return 0;
  return estimateWmtTooltipHeight(stepIndex, false)
    + getWmtDockedBottomOffset(stepIndex)
    + WMT_TOOLTIP_GAP;
}

function getWmtContentBottom(vh, stepIndex, sectionHeight = 0) {
  return vh - getWmtBottomReserve(stepIndex, sectionHeight);
}

function getElementBounds(el) {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width <= 0 && r.height <= 0) return null;
  return { top: r.top, left: r.left, right: r.right, bottom: r.bottom };
}

/** Mobile-only: resolve the DOM node whose height defines each tour step's spotlight. */
function getWmtMobileStepTarget(sectionEl) {
  return sectionEl || null;
}

/** Snap spotlight width to the ref section container; keep target height. */
function composeWmtMobileBounds(sectionEl) {
  const sectionFrame = getElementBounds(sectionEl);
  const target = getWmtMobileStepTarget(sectionEl);
  const targetFrame = getElementBounds(target);

  if (!sectionFrame || !targetFrame) return sectionFrame || targetFrame;

  return {
    top: targetFrame.top,
    left: sectionFrame.left,
    right: sectionFrame.right,
    bottom: targetFrame.bottom,
  };
}

function getWmtRawSectionBounds(sectionEl) {
  const fallback = getElementBounds(sectionEl);
  if (!fallback) {
    const r = sectionEl.getBoundingClientRect();
    return { top: r.top, left: r.left, right: r.right, bottom: r.bottom };
  }

  if (!isWmtMobileLayout()) return fallback;

  return composeWmtMobileBounds(sectionEl) || fallback;
}

function measureWmtRect(stepIndex, sectionRefs) {
  const el = sectionRefs[stepIndex]?.current;
  if (!el) return null;

  const r = getWmtRawSectionBounds(el);
  const padding = getWmtSectionPadding(stepIndex);
  const { vw, vh } = getWmtViewportMetrics();
  const sectionHeight = Math.max(0, r.bottom - r.top);
  // Keep spotlight above docked tooltips so arrows/content are never clipped by the card edge.
  const contentBottom = getWmtContentBottom(vh, stepIndex, sectionHeight);

  const rawTop = r.top - padding;
  const rawBottom = r.bottom + padding;
  const rawLeft = r.left - padding;
  const rawRight = r.right + padding;

  const top = Math.max(0, Math.min(contentBottom, rawTop));
  const bottom = Math.max(top, Math.min(contentBottom, rawBottom));
  const left = Math.max(0, Math.min(vw, rawLeft));
  const right = Math.max(left, Math.min(vw, rawRight));

  if (bottom <= top || right <= left) {
    if (isWmtMobileLayout() || shouldDockWmtTooltip(stepIndex, sectionHeight)) {
      return createWmtHiddenSpotlightRect();
    }
    return null;
  }

  return {
    top,
    left,
    width: right - left,
    height: bottom - top,
    bottom,
    right,
    spotlightVisible: true,
  };
}

/** Clamp tooltip top so the card (+ arrow) stays inside the viewport. */
function clampWmtTooltipTop(preferredTop, tooltipHeight, vh, showAbove) {
  const minTop = WMT_VIEWPORT_MARGIN + (showAbove ? 0 : WMT_ARROW_SIZE);
  const maxBottom = vh - WMT_VIEWPORT_MARGIN - (showAbove ? WMT_ARROW_SIZE : 0);
  const available = Math.max(WMT_TOOLTIP_MIN_HEIGHT, maxBottom - minTop);
  const height = Math.min(tooltipHeight, available);
  const maxTop = Math.max(minTop, maxBottom - height);
  const top = Math.max(minTop, Math.min(preferredTop, maxTop));
  return {
    top,
    maxHeight: Math.max(WMT_TOOLTIP_MIN_HEIGHT, maxBottom - top),
  };
}

function computeWmtTooltipLayout(rect, stepIndex, sectionHeight = 0) {
  const { vw, vh } = getWmtViewportMetrics();
  const tooltipWidth = getWmtTooltipWidth(vw);
  // Stacked layouts, chart steps, and sections taller than the viewport dock the tooltip
  // so it never covers the highlighted card.
  const dockBottom = shouldDockWmtTooltip(stepIndex, sectionHeight);

  if (dockBottom) {
    const dockBottomOffset = getWmtDockedBottomOffset(stepIndex);
    return {
      tooltipWidth,
      tooltipLeft: 0,
      arrowLeft: 0,
      showAbove: false,
      dockBottom: true,
      maxHeight: Math.max(
        WMT_TOOLTIP_MIN_HEIGHT,
        vh - dockBottomOffset - WMT_VIEWPORT_MARGIN,
      ),
      positionStyle: { bottom: dockBottomOffset },
    };
  }

  // Use collapsed height so Read More / Read Less does not shift tooltip position.
  const tooltipHeight = estimateWmtTooltipHeight(stepIndex, false);
  const sectionCenterX = rect.left + rect.width / 2;
  const tooltipLeft = Math.max(
    WMT_VIEWPORT_MARGIN,
    Math.min(sectionCenterX - tooltipWidth / 2, vw - tooltipWidth - WMT_VIEWPORT_MARGIN)
  );
  const arrowLeft = Math.max(
    12,
    Math.min(sectionCenterX - tooltipLeft - 10, tooltipWidth - 30)
  );
  const minSpace = tooltipHeight + WMT_TOOLTIP_GAP + WMT_ARROW_SIZE;

  const spaceBelow = vh - rect.bottom - WMT_VIEWPORT_MARGIN - WMT_ARROW_SIZE;
  const spaceAbove = rect.top - WMT_VIEWPORT_MARGIN - WMT_ARROW_SIZE;
  let showAbove = spaceBelow < minSpace && spaceAbove >= spaceBelow;

  if (!showAbove && rect.bottom + minSpace > vh - WMT_VIEWPORT_MARGIN) {
    showAbove = spaceAbove >= minSpace;
  }

  // If the preferred side still cannot fit, pick the side with more room.
  if (showAbove && spaceAbove < minSpace && spaceBelow > spaceAbove) {
    showAbove = false;
  } else if (!showAbove && spaceBelow < minSpace && spaceAbove > spaceBelow) {
    showAbove = true;
  }

  const preferredTop = showAbove
    ? rect.top - WMT_TOOLTIP_GAP - tooltipHeight
    : rect.bottom + WMT_TOOLTIP_GAP;
  const { top, maxHeight } = clampWmtTooltipTop(preferredTop, tooltipHeight, vh, showAbove);

  return {
    tooltipWidth,
    tooltipLeft,
    arrowLeft,
    showAbove,
    dockBottom: false,
    maxHeight,
    positionStyle: { top },
  };
}

function nudgeWmtSection(el, stepIndex, bottomReserve = 0, topPadding = WMT_SECTION_PADDING) {
  const rawBounds = getWmtRawSectionBounds(el);
  const r = rawBounds || el.getBoundingClientRect();
  const scrollParents = getScrollableAncestors(el);
  const { vh } = getWmtViewportMetrics();
  const maxBottom = vh - bottomReserve;
  const sectionHeight = r.bottom - r.top;
  const contentZoneHeight = maxBottom - topPadding;

  let delta = r.top - topPadding;
  const docked = bottomReserve > 0;

  if (docked || isWmtMobileLayout()) {
    // Tall sections: keep the top in view so the spotlight sits above the tooltip.
    // Short sections that fit but sit too low: shift up once.
    if (sectionHeight <= contentZoneHeight && r.bottom > maxBottom) {
      delta += r.bottom - maxBottom;
    }
  } else if (r.bottom > maxBottom) {
    delta += r.bottom - maxBottom;
  }

  if (Math.abs(delta) <= 1) return;

  if (scrollParents.length > 0) {
    scrollParents[0].scrollTop += delta;
  } else {
    window.scrollBy({ top: delta, behavior: 'auto' });
  }
}

function WalkMeThroughOverlay({ stepIndex, sectionRefs, onNext, onBack, onFinish, onClose }) {
  const [rect, setRect] = useState(null);
  const [stepChanging, setStepChanging] = useState(false);
  const [scrollTracking, setScrollTracking] = useState(false);
  const [settlePulse, setSettlePulse] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(true);
  const [descExpanded, setDescExpanded] = useState(false);
  const [descInteracting, setDescInteracting] = useState(false);
  const [lockedTooltipLayout, setLockedTooltipLayout] = useState(null);
  const pauseLayoutTrackingRef = useRef(false);
  const isFirstStepRef = useRef(true);
  const stepTimersRef = useRef([]);
  const scrollTrackRafRef = useRef(null);
  const tooltipRef = useRef(null);
  const descInteractTimerRef = useRef(null);
  const lockedLayoutStepRef = useRef(-1);
  // Stable ref for keyboard handler to avoid stale-closure churn on the event listener.
  const keyNavRef = useRef({});

  const clearStepTimers = useCallback(() => {
    stepTimersRef.current.forEach((timerId) => {
      clearTimeout(timerId);
    });
    stepTimersRef.current = [];
  }, []);

  const updateRect = useCallback(() => {
    const nextRect = measureWmtRect(stepIndex, sectionRefs);
    if (nextRect) {
      setRect(nextRect);
      return;
    }
    if (isWmtMobileLayout()) {
      setRect(createWmtHiddenSpotlightRect());
    }
  }, [stepIndex, sectionRefs]);

  useEffect(() => {
    lockedLayoutStepRef.current = -1;
    setDescExpanded(false);
    setDescInteracting(false);
    setLockedTooltipLayout(null);
  }, [stepIndex]);

  const handleDescToggle = useCallback(() => {
    if (descInteractTimerRef.current != null) {
      clearTimeout(descInteractTimerRef.current);
    }
    setDescInteracting(true);
    setDescExpanded((prev) => !prev);
    descInteractTimerRef.current = setTimeout(() => {
      setDescInteracting(false);
      descInteractTimerRef.current = null;
    }, 48);
  }, []);

  useEffect(() => () => {
    if (descInteractTimerRef.current != null) {
      clearTimeout(descInteractTimerRef.current);
    }
  }, []);

  const stopScrollTracking = useCallback(() => {
    if (scrollTrackRafRef.current != null) {
      cancelAnimationFrame(scrollTrackRafRef.current);
      scrollTrackRafRef.current = null;
    }
    setScrollTracking(false);
  }, []);

  const scheduleStepTimer = useCallback((fn, delay) => {
    const timerId = setTimeout(fn, delay);
    stepTimersRef.current.push(timerId);
    return timerId;
  }, []);

  const startScrollTracking = useCallback(() => {
    stopScrollTracking();
    setScrollTracking(true);

    const track = () => {
      updateRect();
      scrollTrackRafRef.current = requestAnimationFrame(track);
    };

    scrollTrackRafRef.current = requestAnimationFrame(track);
  }, [stopScrollTracking, updateRect]);

  useEffect(() => {
    const el = sectionRefs[stepIndex]?.current;
    if (!el) return undefined;

    clearStepTimers();
    stopScrollTracking();
    setSettlePulse(false);

    const isFirstStep = isFirstStepRef.current;
    if (isFirstStep) {
      isFirstStepRef.current = false;
      pauseLayoutTrackingRef.current = true;
    } else {
      setStepChanging(true);
      setTooltipVisible(false);
      pauseLayoutTrackingRef.current = true;
    }

    const revealStep = () => {
      stopScrollTracking();
      updateRect();
      setStepChanging(false);

      if (isFirstStep) {
        pauseLayoutTrackingRef.current = false;
        requestAnimationFrame(() => {
          setTooltipVisible(true);
        });
        return;
      }

      setSettlePulse(true);
      scheduleStepTimer(() => {
        setSettlePulse(false);
        pauseLayoutTrackingRef.current = false;
      }, WMT_MORPH_MS);
      scheduleStepTimer(() => {
        setTooltipVisible(true);
      }, WMT_TOOLTIP_IN_MS);
    };

    const beginScroll = () => {
      if (!isFirstStep || isWmtMobileLayout() || isWmtCompactLayout()) {
        if (!isFirstStep) {
          pauseLayoutTrackingRef.current = false;
        }
        startScrollTracking();
      }

      el.scrollIntoView({
        behavior: isFirstStep ? 'auto' : 'smooth',
        block: getWmtScrollBlock(),
      });

      // Keep the spotlight clear of a sticky shell header and of a docked tooltip.
      const sectionHeight = getUnclippedSectionHeight(el);
      const bottomReserve = getWmtBottomReserve(stepIndex, sectionHeight);
      const topPadding = getWmtSectionPadding(stepIndex) + getWmtStickyHeaderOffset();
      requestAnimationFrame(() => nudgeWmtSection(el, stepIndex, bottomReserve, topPadding));
      scheduleStepTimer(() => nudgeWmtSection(el, stepIndex, bottomReserve, topPadding), 420);

      let settleTimer;
      const onScrollActivity = () => {
        clearTimeout(settleTimer);
        settleTimer = setTimeout(revealStep, WMT_SCROLL_SETTLE_MS);
      };

      window.addEventListener('scroll', onScrollActivity, { passive: true, capture: true });

      const scrollParents = getScrollableAncestors(el);
      scrollParents.forEach((parent) => {
        parent.addEventListener('scroll', onScrollActivity, { passive: true });
      });

      onScrollActivity();
      scheduleStepTimer(revealStep, isFirstStep ? 50 : WMT_STEP_CHANGE_MS);

      return () => {
        clearTimeout(settleTimer);
        window.removeEventListener('scroll', onScrollActivity, { capture: true });
        scrollParents.forEach((parent) => {
          parent.removeEventListener('scroll', onScrollActivity);
        });
      };
    };

    let removeScrollListeners;
    if (isFirstStep) {
      removeScrollListeners = beginScroll();
    } else {
      scheduleStepTimer(() => {
        removeScrollListeners = beginScroll();
      }, WMT_TOOLTIP_OUT_MS);
    }

    return () => {
      clearStepTimers();
      stopScrollTracking();
      removeScrollListeners?.();
      pauseLayoutTrackingRef.current = false;
    };
  }, [
    stepIndex,
    sectionRefs,
    updateRect,
    clearStepTimers,
    scheduleStepTimer,
    startScrollTracking,
    stopScrollTracking,
  ]);

  useEffect(() => {
    const el = sectionRefs[stepIndex]?.current;
    const onScrollOrResize = () => {
      if (pauseLayoutTrackingRef.current) return;
      updateRect();
    };

    window.addEventListener('scroll', onScrollOrResize, { passive: true, capture: true });
    window.addEventListener('resize', onScrollOrResize);

    const visualViewport = window.visualViewport;
    if (isWmtMobileLayout() && visualViewport) {
      visualViewport.addEventListener('scroll', onScrollOrResize, { passive: true });
      visualViewport.addEventListener('resize', onScrollOrResize, { passive: true });
    }

    const scrollParents = el ? getScrollableAncestors(el) : [];
    scrollParents.forEach((parent) => {
      parent.addEventListener('scroll', onScrollOrResize, { passive: true });
    });

    let resizeObserver;
    if (el && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(onScrollOrResize);
      resizeObserver.observe(el);
    }

    if (!pauseLayoutTrackingRef.current) {
      updateRect();
    }

    return () => {
      window.removeEventListener('scroll', onScrollOrResize, { capture: true });
      window.removeEventListener('resize', onScrollOrResize);
      if (visualViewport) {
        visualViewport.removeEventListener('scroll', onScrollOrResize);
        visualViewport.removeEventListener('resize', onScrollOrResize);
      }
      scrollParents.forEach((parent) => {
        parent.removeEventListener('scroll', onScrollOrResize);
      });
      resizeObserver?.disconnect();
    };
  }, [stepIndex, sectionRefs, updateRect]);

  useLayoutEffect(() => {
    if (!tooltipVisible || !rect) return;
    if (lockedLayoutStepRef.current === stepIndex && lockedTooltipLayout) return;
    lockedLayoutStepRef.current = stepIndex;
    setLockedTooltipLayout(computeWmtTooltipLayout(
      rect,
      stepIndex,
      getUnclippedSectionHeight(sectionRefs[stepIndex]?.current),
    ));
  }, [tooltipVisible, rect, stepIndex, lockedTooltipLayout, sectionRefs]);

  // Invalidate locked layout on viewport resize / orientation change so the tooltip
  // repositions correctly rather than staying anchored to stale coordinates.
  useEffect(() => {
    const onViewportChange = () => {
      wmtSafeAreaBottomCache = { vw: -1, value: 0 };
      lockedLayoutStepRef.current = -1;
      setLockedTooltipLayout(null);
    };
    window.addEventListener('resize', onViewportChange);
    window.screen?.orientation?.addEventListener?.('change', onViewportChange);

    const visualViewport = window.visualViewport;
    if (isWmtMobileLayout() && visualViewport) {
      visualViewport.addEventListener('resize', onViewportChange, { passive: true });
    }

    return () => {
      window.removeEventListener('resize', onViewportChange);
      window.screen?.orientation?.removeEventListener?.('change', onViewportChange);
      if (visualViewport) {
        visualViewport.removeEventListener('resize', onViewportChange);
      }
    };
  }, []);

  // Keyboard navigation: Escape exits tour; ←/→ arrows step back/forward.
  // Uses a stable ref so the single event listener is never re-registered.
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === walkthroughSteps.length - 1;
  keyNavRef.current = { onClose, onNext, onBack, onFinish, isFirst, isLast, stepChanging, scrollTracking };

  useEffect(() => {
    const handleKeyDown = (e) => {
      const nav = keyNavRef.current;
      if (e.key === 'Escape') {
        nav.onClose?.();
        return;
      }
      if (nav.stepChanging || nav.scrollTracking) return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (nav.isLast) nav.onFinish?.();
        else nav.onNext?.();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (!nav.isFirst) nav.onBack?.();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (typeof document === 'undefined') return null;
  const sectionHeight = getUnclippedSectionHeight(sectionRefs[stepIndex]?.current);
  if (!rect && !shouldDockWmtTooltip(stepIndex, sectionHeight)) return null;

  const step = walkthroughSteps[stepIndex];
  if (!step) return null;
  const { summary: descriptionSummary, full: descriptionFull } = getWmtStepDescriptionContent(step);
  const hasReadMore = wmtStepHasReadMore(step) && descriptionFull;
  const mobileLayout = isWmtMobileLayout();
  const passThroughStyle = getWmtPassThroughStyle();
  const spotlightVisible = isWmtSpotlightVisible(rect);
  const coverPassThroughStyle = mobileLayout ? passThroughStyle : null;
  const { vh } = getWmtViewportMetrics();
  const contentBottom = getWmtContentBottom(vh, stepIndex, sectionHeight);

  const liveTooltipLayout = computeWmtTooltipLayout(
    rect || createWmtHiddenSpotlightRect(),
    stepIndex,
    sectionHeight,
  );

  const {
    tooltipWidth,
    tooltipLeft,
    arrowLeft,
    showAbove,
    dockBottom,
    maxHeight: layoutMaxHeight,
    positionStyle: tooltipPositionStyle,
  } = lockedTooltipLayout || liveTooltipLayout;

  const bottomFillHeight = mobileLayout || dockBottom
    ? getWmtBottomReserve(stepIndex, sectionHeight)
    : 0;

  // Keep the chrome (dots / buttons) visible; scroll only the text body when tall.
  const tooltipMaxHeight = Number(layoutMaxHeight);
  const tooltipBodyMaxHeightStyle = (() => {
    if (!Number.isFinite(tooltipMaxHeight)) return null;
    // Reserve space for padding + button row so the card itself is never clipped.
    const chromeReserve = descExpanded && hasReadMore ? 96 : 88;
    return {
      maxHeight: Math.max(120, tooltipMaxHeight - chromeReserve),
    };
  })();

  const highlightStyle = rect ? {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
  } : createWmtHiddenSpotlightRect();

  // Fallback dim covering the reserved content zone when the spotlight rect isn't
  // available yet — applies on mobile and on desktop-docked steps (chart / checkup)
  // so the background is never left undimmed/unblocked.
  const contentCoverFallbackStyle = (mobileLayout || dockBottom) ? {
    top: 0,
    left: 0,
    right: 0,
    height: contentBottom,
    ...coverPassThroughStyle,
  } : null;

  const overlayClassName = [
    styles.wmtOverlay,
    mobileLayout ? styles.wmtOverlayMobileScroll : '',
    stepChanging ? styles.wmtStepChanging : '',
    scrollTracking ? styles.wmtScrollTracking : '',
    settlePulse ? styles.wmtSettling : '',
    descInteracting ? styles.wmtOverlayDescInteracting : '',
  ].filter(Boolean).join(' ');

  const tooltipClassName = [
    styles.wmtTooltip,
    dockBottom ? styles.wmtTooltipDocked : '',
    descExpanded && hasReadMore ? styles.wmtTooltipDescExpanded : '',
    tooltipVisible ? styles.wmtTooltipVisible : styles.wmtTooltipHidden,
  ].join(' ');

  return createPortal(
    <div
      className={overlayClassName}
      role="dialog"
      aria-modal="true"
      aria-label={`Tour: ${step.title}`}
    >
      {/* Backdrop is visual-only; cover panels block clicks outside the tooltip buttons (desktop) */}
      <div className={styles.wmtBackdrop} aria-hidden="true" />

      {bottomFillHeight > 0 ? (
        <div
          className={styles.wmtViewportBottomFill}
          style={{ height: bottomFillHeight }}
          aria-hidden="true"
        />
      ) : null}

      {!spotlightVisible && contentCoverFallbackStyle ? (
        <div
          className={`${styles.wmtCover} ${styles.wmtMobileContentCover}`}
          style={contentCoverFallbackStyle}
          aria-hidden="true"
        />
      ) : null}

      {/* Spotlight cutout around the active section; tooltip band stays dimmed (not part of the hole) */}
      {spotlightVisible && (
        <>
          <div
            className={styles.wmtCover}
            style={{ top: 0, left: 0, right: 0, height: rect.top, ...coverPassThroughStyle }}
            aria-hidden="true"
          />
          <div
            className={styles.wmtCover}
            style={{ top: rect.bottom, left: 0, right: 0, bottom: 0, ...coverPassThroughStyle }}
            aria-hidden="true"
          />
          <div
            className={styles.wmtCover}
            style={{ top: rect.top, left: 0, width: rect.left, height: rect.height, ...coverPassThroughStyle }}
            aria-hidden="true"
          />
          <div
            className={styles.wmtCover}
            style={{ top: rect.top, left: rect.right, right: 0, height: rect.height, ...coverPassThroughStyle }}
            aria-hidden="true"
          />
        </>
      )}

      {spotlightVisible && (
        <>
          <div
            className={`${styles.wmtHighlightFrame} ${settlePulse ? styles.wmtHighlightPulse : ''}`}
            style={highlightStyle}
          />

          {!mobileLayout && (
            <div className={styles.wmtHighlightBlocker} style={highlightStyle} aria-hidden="true" />
          )}
        </>
      )}

      {/* Tooltip — fixed to the bottom on mobile while the page scrolls underneath */}
      <div
        ref={tooltipRef}
        className={tooltipClassName}
        style={{
          ...(dockBottom ? {} : { left: tooltipLeft, width: tooltipWidth }),
          ...tooltipPositionStyle,
        }}
      >
        {!showAbove && !dockBottom && <div className={styles.wmtArrowUp} style={{ left: arrowLeft }} />}

        <div key={stepIndex} className={styles.wmtStepContent}>
          <div
            className={styles.wmtTooltipBody}
            style={tooltipBodyMaxHeightStyle ? {
              maxHeight: tooltipBodyMaxHeightStyle.maxHeight,
              overflowY: 'auto',
              overscrollBehavior: 'contain',
            } : undefined}
          >
            {/* Step progress dots + dismiss button */}
            <div className={styles.wmtStepHeader}>
              <div className={styles.wmtStepDots}>
                {walkthroughSteps.map((_, i) => (
                  <div
                    key={i}
                    className={`${styles.wmtDot} ${i === stepIndex ? styles.wmtDotActive : ''}`}
                  />
                ))}
              </div>
              {onClose && (
                <button
                  type="button"
                  className={styles.wmtCloseBtn}
                  onClick={onClose}
                  aria-label="Exit tour"
                >
                  ✕
                </button>
              )}
            </div>

            <div className={styles.wmtTitle}>{step.title}</div>
            <div className={styles.wmtDescBlock}>
              <div className={styles.wmtDesc}>{descriptionSummary}</div>
              {hasReadMore && descExpanded ? (
                <div className={styles.wmtDescDetails}>
                  {descriptionFull}
                </div>
              ) : null}
              {hasReadMore ? (
                <button
                  type="button"
                  className={styles.wmtReadMoreLink}
                  onClick={handleDescToggle}
                  aria-expanded={descExpanded}
                >
                  {descExpanded ? 'READ LESS' : 'READ MORE'}
                </button>
              ) : null}
            </div>
          </div>

          <div className={styles.wmtButtons}>
            {!isFirst && (
              <button
                className={styles.wmtBackBtn}
                type="button"
                onClick={onBack}
                disabled={stepChanging || scrollTracking}
              >
                Back
              </button>
            )}
            {isLast ? (
              <button
                className={styles.wmtNextBtn}
                type="button"
                onClick={onFinish}
                disabled={stepChanging || scrollTracking}
              >
                Finish
              </button>
            ) : (
              <button
                className={styles.wmtNextBtn}
                type="button"
                onClick={onNext}
                disabled={stepChanging || scrollTracking}
              >
                Next
              </button>
            )}
          </div>
        </div>

        {showAbove && !dockBottom && <div className={styles.wmtArrowDown} style={{ left: arrowLeft }} />}
      </div>
    </div>,
    document.body
  );
}

/* Chart heights by viewport */
const CHART_HEIGHT_DESKTOP = 400;
const CHART_HEIGHT_TABLET = 280;
const CHART_HEIGHT_MOBILE = 240;
const CHART_HEIGHT_SMALL_MOBILE = 210;

// Chart configuration constants
const CHART_CONFIG = {
  margins: {
    projectedNetWorth: { left: 10, right: 10, top: 20, bottom: 10 },
    cashFlowBarChart: { left: 20, right: 10, top: 20, bottom: 10 }
  },
  /* Mobile (≤768px): small padding so axis/legend labels don't sit flush or overflow */
  mobileMargins: {
    projectedNetWorth: { left: 8, right: 8, top: 8, bottom: 8 },
    cashFlowBarChart: { left: 8, right: 8, top: 8, bottom: 8 }
  },
  colors: {
    // Match categoryChip icon colors; hues are spaced so stacked/grouped bars stay distinct.
    realEstate: '#d97b5c',
    otherAssets: '#4558a0',
    liabilities: '#7a4b68',
    currentDollar: '#156a73',
    futureDollar: '#c45688',
    expensesLine: '#32424a',
    workIncome: '#156a73',
    socialSecurity: '#d97b5c',
    pension: '#6f4d9c',
    annuity: '#c45688',
    rmd: '#a4853c',
    collegeSavingsDrawdown: '#3f78b5',
    savingsDrawdown: '#358259'
  },
  legend: {
    fontSize: '0.9rem',
    fontSizeTablet: '0.74rem',
    fontSizeMobile: '0.7rem',
    fontSizeSmallMobile: '0.66rem',
    fontFamily: 'var(--font-numeric)',
    fontWeight: 500,
    fill: '#333'
  },
  axis: {
    tickLabel: {
      fill: 'rgba(0, 0, 0, 0.87)',
      fontSize: '0.8rem',
      fontSizeTablet: '0.7rem',
      fontSizeMobile: '0.66rem',
      fontSizeSmallMobile: '0.62rem',
      fontFamily: 'var(--font-numeric)',
      fontWeight: 400
    }
  },
  /** Easing/duration for polished dashboard charts (bars, line overlays, line mask reveal). */
  polishedChartAnimation: {
    duration: 1400,
    easing: 'cubic-bezier(0.22, 1, 0.36, 1)'
  },
  // Cash flow bar chart: series order and labels (colors come from CHART_CONFIG.colors)
  cashFlowSeries: [
    { dataKey: 'workIncome', label: 'Work Income' },
    { dataKey: 'socialSecurity', label: 'Social Security' },
    { dataKey: 'pension', label: 'Pension' },
    { dataKey: 'annuity', label: 'Annuity' },
    { dataKey: 'rmd', label: 'RMD' },
    { dataKey: 'collegeSavingsDrawdown', label: 'College Savings Drawdown' },
    { dataKey: 'savingsDrawdown', label: 'Savings Drawdown' }
  ],
  netWorthSeries: [
    { dataKey: 'realEstate', label: 'Real Estate Assets', stack: 'assets' },
    { dataKey: 'otherAssets', label: 'Investment Assets', stack: 'assets' },
    { dataKey: 'liabilities', label: 'Liabilities' },
    { dataKey: 'currentDollar', label: 'NW (Current)' },
    { dataKey: 'futureDollar', label: 'NW (Future)' },
  ],
  /** Minimum share of stack height for a non-zero segment so small values (e.g. Social Security) stay visible. */
  minStackSegmentRatio: 0.03
};

const CASH_FLOW_FILTER_ALL = 'all';
const CASH_FLOW_EXPENSES_KEY = 'expenses';
const NET_WORTH_FILTER_ALL = 'all';

const CASH_FLOW_FILTER_OPTIONS = [
  { value: CASH_FLOW_FILTER_ALL, label: 'All' },
  ...CHART_CONFIG.cashFlowSeries.map(({ dataKey, label }) => ({ value: dataKey, label })),
  { value: CASH_FLOW_EXPENSES_KEY, label: 'Expenses & Taxes' },
];

const NET_WORTH_FILTER_OPTIONS = [
  { value: NET_WORTH_FILTER_ALL, label: 'All Assets' },
  ...CHART_CONFIG.netWorthSeries.map(({ dataKey, label }) => ({ value: dataKey, label })),
];

const MOBILE_BREAKPOINT = 1024;
const CONFETTI_DELAY_MS = 2500;

function getResponsiveChartHeight(width) {
  if (width <= BREAKPOINT_SMALL_MOBILE) return CHART_HEIGHT_SMALL_MOBILE;
  if (width <= BREAKPOINT_TABLET) return CHART_HEIGHT_MOBILE;
  if (width <= MOBILE_BREAKPOINT) return CHART_HEIGHT_TABLET;
  return CHART_HEIGHT_DESKTOP;
}

function getResponsiveAxisFontSize(width) {
  if (width <= BREAKPOINT_SMALL_MOBILE) return CHART_CONFIG.axis.tickLabel.fontSizeSmallMobile;
  if (width <= BREAKPOINT_TABLET) return CHART_CONFIG.axis.tickLabel.fontSizeMobile;
  if (width <= MOBILE_BREAKPOINT) return CHART_CONFIG.axis.tickLabel.fontSizeTablet;
  return CHART_CONFIG.axis.tickLabel.fontSize;
}

function getResponsiveLegendFontSize(width) {
  if (width <= BREAKPOINT_SMALL_MOBILE) return CHART_CONFIG.legend.fontSizeSmallMobile;
  if (width <= BREAKPOINT_TABLET) return CHART_CONFIG.legend.fontSizeMobile;
  if (width <= MOBILE_BREAKPOINT) return CHART_CONFIG.legend.fontSizeTablet;
  return CHART_CONFIG.legend.fontSize;
}

/**
 * Tooltip appearance for chart hovers. Applied via ChartsTooltip `sx` / BarChart `slotProps.tooltip`
 * (not the chart wrapper `sx`), so styles reach the portaled Popper and its inner paper/table.
 * Keeps the original simple card look; dollar amounts stay right-aligned.
 */
function getDashboardChartsTooltipSx(chartLegendFontSize) {
  return {
    outline: 'none',
    zIndex: DASHBOARD_CHARTS_TOOLTIP_Z_INDEX,
    '& .MuiChartsTooltip-paper': {
      backgroundColor: 'rgba(255, 255, 255, 0.98)',
      border: '1px solid rgba(4, 71, 102, 0.1)',
      borderRadius: '10px',
      boxShadow: '0 8px 28px rgba(4, 71, 102, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06)',
      padding: '12px 16px',
      backdropFilter: 'blur(10px)',
      fontSize: chartLegendFontSize
    },
    '& .MuiChartsTooltip-valueCell': {
      textAlign: 'right'
    },
    '& .MuiChartsTooltip-axisValueCell': {
      textAlign: 'right'
    }
  };
}

/** Shared ChartsTooltip / BarChart tooltip props for growth visualization charts. */
function getDashboardChartsTooltipProps(chartLegendFontSize) {
  return {
    sx: getDashboardChartsTooltipSx(chartLegendFontSize),
  };
}

/** Axis ticks stay a number; the hover header reads "Age: 50". */
function formatChartAgeAxisValue(value, context) {
  if (context?.location === 'tooltip') return `Age: ${value}`;
  return context?.defaultTickLabel ?? `${value}`;
}

/** Shared MUI X Charts sx for Projected Net Worth and Lifetime Income (grid, axes, legend, hovers, line polish). */
function getPolishedDashboardChartSx(chartAxisFontSize, chartLegendFontSize) {
  return {
    width: '100%',
    maxWidth: '100%',
    '& .MuiChartsWrapper-root': {
      width: '100% !important',
      maxWidth: '100% !important',
      position: 'relative'
    },
    '& .MuiChartsAxis-tickLabel': {
      fill: CHART_CONFIG.axis.tickLabel.fill,
      fontSize: chartAxisFontSize,
      fontFamily: CHART_CONFIG.axis.tickLabel.fontFamily,
      fontWeight: CHART_CONFIG.axis.tickLabel.fontWeight,
      letterSpacing: '0.01em'
    },
    '& .MuiChartsAxis-line': {
      stroke: 'rgba(4, 71, 102, 0.14)',
      strokeWidth: 1
    },
    '& .MuiChartsAxis-tick': {
      stroke: 'rgba(4, 71, 102, 0.2)'
    },
    '& .MuiChartsAxis-label': {
      fill: CHART_CONFIG.axis.tickLabel.fill,
      fontSize: chartLegendFontSize,
      fontFamily: CHART_CONFIG.legend.fontFamily,
      fontWeight: CHART_CONFIG.legend.fontWeight
    },
    '& .MuiChartsGrid-line': {
      stroke: 'rgba(4, 71, 102, 0.07)',
      strokeDasharray: '2 6'
    },
    '& .MuiChartsAxisHighlight-root': {
      fill: 'rgba(15, 90, 110, 0.05)',
      fillOpacity: 1
    },
    '& .MuiChartsLegend-label': { fontSize: chartLegendFontSize, letterSpacing: '0.01em' },
    '& .MuiChartsBar-root': {
      transition: 'opacity 0.25s cubic-bezier(0.33, 1, 0.68, 1), filter 0.25s ease',
      '&:hover': {
        opacity: 0.92,
        filter: 'brightness(1.02)'
      }
    },
    '& .MuiLineElement-root': {
      strokeWidth: 2.75,
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      filter: 'drop-shadow(0 1px 3px rgba(50, 66, 74, 0.18))'
    },
    '& .MuiAppearingMask-animate': {
      animationDuration: '1.15s',
      animationTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)'
    }
  };
}
const DEFAULT_CHART_BOUNDS = { min: 0, max: 1000 };

function safeFormatCurrency(value) {
  if (value === null || value === undefined || isNaN(value) || !isFinite(value)) {
    return formatCurrency(0);
  }
  return formatCurrency(Number(value));
}

function formatBarChartValue(value) {
  return value > 0 ? safeFormatCurrency(value) : null;
}

/**
 * Dollar height of the "College Savings Drawdown" segment in the lifetime income stack.
 *
 * Backend (baseline `charts`, not `mc_failure_trace`): the flow from 529/education accounts is
 * `charts.income[].education_savings_withdrawal` only (real dollars). The remaining account balance is
 * `charts.assets[].education_savings`—a stock, not income—so it must not be added to this segment or combined
 * with the withdrawal for display totals.
 *
 * `projectedCashFlow` rows expose the same value as `educationSavingsWithdrawal` and mirror it on
 * `collegeSavingsDrawdown` for the bar `dataKey`. Use one field only so we never double-count.
 */
function getCashFlowCollegeSavingsDrawdownTotal(point) {
  if (!point || typeof point !== 'object') return 0;
  if (Object.prototype.hasOwnProperty.call(point, 'educationSavingsWithdrawal')) {
    return Math.max(0, Math.round(Number(point.educationSavingsWithdrawal) || 0));
  }
  return Math.max(0, Math.round(Number(point.collegeSavingsDrawdown) || 0));
}

/** Actual (non min-gap) cash-flow rows for single-series chart filters. */
function buildCashFlowActualDataset(rawData) {
  if (!Array.isArray(rawData) || rawData.length === 0) return [];
  return rawData.map((item) => ({
    age: Number(item.age) || 0,
    workIncome: Math.max(0, Math.round(Number(item.workIncome ?? item.income) || 0)),
    socialSecurity: Math.max(0, Math.round(Number(item.socialSecurity) || 0)),
    pension: Math.max(0, Math.round(Number(item.pension) || 0)),
    annuity: Math.max(0, Math.round(Number(item.annuity) || 0)),
    rmd: Math.max(0, Math.round(Number(item.rmd) || 0)),
    collegeSavingsDrawdown: Math.max(0, Math.round(getCashFlowCollegeSavingsDrawdownTotal(item))),
    savingsDrawdown: Math.max(0, Math.round(Number(item.savingsDrawdown) || 0)),
    expenses: Math.max(0, Math.round(Number(item.expenses) || 0)),
  }));
}

/**
 * Builds the cash flow bar chart dataset with a minimum visual height for small segments
 * so that e.g. Social Security stays visible when dwarfed by RMD/Savings Drawdown.
 * Returns display values; actual values should be shown in tooltip via raw data lookup.
 */
function buildCashFlowBarDatasetWithMinGap(rawData, minRatio = CHART_CONFIG.minStackSegmentRatio) {
  if (!Array.isArray(rawData) || rawData.length === 0) return [];
  return rawData.map((item) => {
    const age = Number(item.age) || 0;
    const workIncome = Math.max(0, Math.round(Number(item.workIncome ?? item.income) || 0));
    const socialSecurity = Math.max(0, Math.round(Number(item.socialSecurity) || 0));
    const pension = Math.max(0, Math.round(Number(item.pension) || 0));
    const annuity = Math.max(0, Math.round(Number(item.annuity) || 0));
    const rmd = Math.max(0, Math.round(Number(item.rmd) || 0));
    const savingsDrawdown = Math.max(0, Math.round(Number(item.savingsDrawdown) || 0));
    const collegeSavingsDrawdown = Math.max(0, Math.round(getCashFlowCollegeSavingsDrawdownTotal(item)));
    const total = workIncome + socialSecurity + pension + annuity + rmd + collegeSavingsDrawdown + savingsDrawdown;
    const minSegment = total > 0 ? total * minRatio : 0;

    const keys = ['workIncome', 'socialSecurity', 'pension', 'annuity', 'rmd', 'collegeSavingsDrawdown', 'savingsDrawdown'];
    const raw = { workIncome, socialSecurity, pension, annuity, rmd, collegeSavingsDrawdown, savingsDrawdown };
    const minSegRounded = Math.round(minSegment);

    // Step 1: enforce minimum visual height for tiny non-zero segments
    const adjusted = {};
    for (const k of keys) {
      const v = raw[k];
      adjusted[k] = v > 0 && v < minSegment ? minSegRounded : v;
    }

    // Step 2: keep total stack height equal to the real total by reducing the largest segment(s)
    // (otherwise adding minimums would inflate the bar vs the expense chart for that year).
    const sumAdjusted = keys.reduce((s, k) => s + (adjusted[k] || 0), 0);
    let excess = Math.max(0, sumAdjusted - total);
    if (excess > 0 && total > 0) {
      // Compute reducible headroom per segment (can't reduce below min for non-zero, or below 0 for zero).
      const minAllowed = {};
      for (const k of keys) {
        minAllowed[k] = raw[k] > 0 ? (raw[k] < minSegment ? minSegRounded : 0) : 0;
      }

      // Reduce from the segment with the most headroom until excess is gone.
      // Usually this is Savings Drawdown or Work Income.
      for (let guard = 0; guard < 20 && excess > 0; guard++) {
        let bestKey = null;
        let bestHeadroom = 0;
        for (const k of keys) {
          const headroom = Math.max(0, (adjusted[k] || 0) - (minAllowed[k] || 0));
          if (headroom > bestHeadroom) {
            bestHeadroom = headroom;
            bestKey = k;
          }
        }
        if (!bestKey || bestHeadroom <= 0) break;
        const reduceBy = Math.min(excess, bestHeadroom);
        adjusted[bestKey] = (adjusted[bestKey] || 0) - reduceBy;
        excess -= reduceBy;
      }
    }

    return { age, ...adjusted };
  });
}

/** Sum of displayed income stack segments (matches buildCashFlowBarDatasetWithMinGap output). */
function sumCashFlowDisplayStack(item) {
  if (!item) return 0;
  return (item.workIncome || 0) + (item.socialSecurity || 0) + (item.pension || 0) + (item.annuity || 0)
    + (item.rmd || 0) + (item.collegeSavingsDrawdown || 0) + (item.savingsDrawdown || 0);
}

function formatChartValue(value) {
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (absValue >= 10000000) {
    return `${sign}$${(absValue / 1000000).toLocaleString('en-US', { maximumFractionDigits: 0 })}M`;
  }
  if (absValue >= 1000000) {
    return `${sign}$${(absValue / 1000000).toLocaleString('en-US', { maximumFractionDigits: 1 })}M`;
  }
  if (absValue >= 100000) {
    return `${sign}$${(absValue / 1000).toLocaleString('en-US', { maximumFractionDigits: 0 })}K`;
  }
  if (absValue >= 1000) {
    return `${sign}$${(absValue / 1000).toLocaleString('en-US', { maximumFractionDigits: 1 })}K`;
  }
  return `${sign}$${Math.round(absValue).toLocaleString('en-US')}`;
}

function calculateChartBounds(data, getValues) {
  if (!Array.isArray(data) || data.length === 0) return DEFAULT_CHART_BOUNDS;
  if (typeof getValues !== 'function') {
    logger.warn('calculateChartBounds: getValues is not a function');
    return DEFAULT_CHART_BOUNDS;
  }
  try {
    const allValues = data.flatMap(getValues).filter(val =>
      typeof val === 'number' && !isNaN(val) && isFinite(val)
    );
    if (allValues.length === 0) return DEFAULT_CHART_BOUNDS;
    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);
    if (minValue === maxValue) {
      const padding = Math.max(Math.abs(minValue) * 0.1, 100);
      return { min: minValue - padding, max: maxValue + padding };
    }
    return {
      min: minValue < 0 ? minValue * 1.1 : minValue * 0.9,
      max: maxValue * 1.1
    };
  } catch (error) {
    logger.error('Error calculating chart bounds:', error);
    return DEFAULT_CHART_BOUNDS;
  }
}

const DEFAULT_CAREER_SHIFT_RUNWAY = {
  currentMonthlyIncome: 0,
  currentMonthlyExpenses: 0,
  monthlyGap: 0,
  careerShiftDurationMonths: 0,
  runwayValue: FINANCIAL_CONSTANTS.FULLY_COVERED,
  accessibleShortTermLiquidity: 0
};

/** e.g. 98 → "98 months", 98.5 → "98.5 months" (never "98.0 months") */
/**
 * Runs checkup plan generation and save when plan was not already returned by v2 GET / admin onboarding fetch.
 * No-ops if aborted or no token. Errors are logged; does not throw.
 */
function runCheckupPlanIfNeeded(opts) {
  const { fullData, onboardingProgress, useAdminEndpointForSummary, summaryUsername, signal } = opts;
  if (signal?.aborted) return Promise.resolve(null);
  const token = getToken();
  if (!token) return Promise.resolve(null);
  return runCheckupPlanGenerationAndSave({
    fullData,
    onboardingProgress,
    summaryText: '',
    token,
    summaryUsername,
    useAdminEndpointForSummary,
    signal
  }).catch((err) => {
    if (err?.name !== 'AbortError') logger.error('Error saving checkup and action plan:', err);
    return null;
  });
}

/** Wait for fonts + two frames so action-card borders are measured before first paint. */
function waitUntilPaintReady() {
  const fontsReady = document.fonts?.ready ?? Promise.resolve();
  return Promise.resolve(fontsReady)
    .catch(() => {})
    .then(
      () =>
        new Promise((resolve) => {
          requestAnimationFrame(() => requestAnimationFrame(resolve));
        })
    );
}

function DashboardLoadingState({ message }) {
  return (
    <div className={loadingStyles['loading-container']}>
      <div className={loadingStyles['loading-spinner']}>
        <div className={loadingStyles['spinner-ring']} />
        <div className={loadingStyles['spinner-ring']} />
        <div className={loadingStyles['spinner-ring']} />
      </div>
      <p>{message}</p>
    </div>
  );
}

/* ---------------------------------------------------------------------------
   Checkup & Action Plan — helpers and embedded summary (from CheckupAndActionPlanPage)
   --------------------------------------------------------------------------- */

const CHECKUP_NO_DATA = 'No Data';

const CHECKUP_PROGRESS_KEYS = {
  FAMILY_SITUATION: 'family_situation',
  YOUR_YOB: 'your_year_of_birth',
  PARTNER_YOB: 'partner_year_of_birth_of_birth',
  MONTHLY_INCOME_PARTNER: 'monthly_net_income_partner',
  CHILDREN_NUMBER: 'children_number',
  RETIREMENT_AGE: 'retirement_age',
  PARTNER_RETIRE: 'partner_retire',
  SS_BENEFITS_AGE: 'your_social_security_benefits_age',
  PARTNER_SS_AGE: 'partner_social_security_benefits_age',
  PENSION_PLAN: 'pension_plan',
  PENSION_START_AGE: 'pension_income_start_age',
  PENSION_MONTHLY: 'pension_monthly_income',
  STATE: 'state',
  CITY: 'city'
};

const FULL_CHECKUP_PLAN_KEYS = [
  'CareerTransitionAndCashFlowSecondParagraph',
  'CareerTransitionAndCashFlowThirdParagraph',
  'CareerTransitionAndCashFlowFourthParagraph',
  'FinancialDashboardActionCardDescriptionPlaceholders',
];

function formatLongevityScoreDisplay(percentage) {
  return formatLongevityBand(percentage, CHECKUP_NO_DATA);
}

function getFreedomRetirementStatus(score, ageSummary) {
  const scoreDisplay = formatLongevityScoreDisplay(score);
  const hasScore = score != null && typeof score === 'number' && !Number.isNaN(score);
  const baseAgeSummary = ageSummary || 'Your retirement plan is currently being evaluated';

  if (!hasScore) {
    return {
      strongPill: 'Nudge',
      isStrong: false,
      bannerSub: `${baseAgeSummary}.`,
      actionPlanSub: `${baseAgeSummary}. Review your retirement inputs to confirm the Financial Longevity Score.`,
    };
  }

  if (score >= 80) {
    return {
      strongPill: 'Strong',
      isStrong: true,
      bannerSub: `${baseAgeSummary}. You have a very high chance of funding your plan through life.`,
      actionPlanSub: `Financial Longevity Score is ${scoreDisplay}; you have a very high chance of funding your plan through life. Keep monitoring contributions, investment allocation, and major assumptions to stay on track.`,
    };
  }

  if (score >= 40) {
    return {
      strongPill: 'Nudge',
      isStrong: false,
      bannerSub: `${baseAgeSummary}. You are getting close and your plan is on the right track; a few small tweaks could improve your Financial Longevity Score.`,
      actionPlanSub: `Financial Longevity Score is ${scoreDisplay}; you are getting close and your plan is on the right track. Focus on small improvements to contributions, investment allocation, and planning assumptions to move closer to the CFP benchmark for long-term success.`,
    };
  }

  return {
    strongPill: 'Nudge',
    isStrong: false,
    bannerSub: `${baseAgeSummary}. Your Financial Longevity Score has room to grow, so focus on strengthening savings and assumptions.`,
    actionPlanSub: `Financial Longevity Score is ${scoreDisplay}; there is room to grow. Strengthen savings, contributions, and planning assumptions to improve your chance of funding expenses through life.`,
  };
}

function getCareerTransitionPromptHints(ctx) {
  const monthlyGap = Number(ctx.monthlyGap) || 0;
  const liquidity = Number(ctx.liquidity) || 0;
  const totalShortfall = Number(ctx.totalShortfall) || 0;
  const debtRatio = parseFloat(ctx.debtToAssetRatio) || 0;
  const creditCardDebt = Number(ctx.creditCardDebt) || 0;

  const liquiditySufficient = monthlyGap <= 0 || liquidity >= totalShortfall;
  const debtRatioBand = debtRatio >= 40 ? 'high' : debtRatio >= 10 ? 'mid' : debtRatio > 0 ? 'low' : 'none';
  const creditCardBand = creditCardDebt <= 1000 ? 'low' : 'high';

  return {
    liquiditySufficient,
    debtRatioBand,
    creditCardBand,
    hasSurplus: monthlyGap < 0,
    isBalanced: monthlyGap === 0
  };
}

function buildCareerTransitionParagraphsPrompt(ctx) {
  const h = getCareerTransitionPromptHints(ctx);
  const welcomeRaw = (ctx.welcomeMessageAnswer ?? '').toString().trim();
  const welcomeForPrompt = JSON.stringify(welcomeRaw);
  const shiftFramingRule = welcomeRaw
    ? `The user described their situation in onboarding as welcome_message_answer (exact string for your reference only—paraphrase naturally in plain language, do not paste as a long quote): ${welcomeForPrompt}. Across every career-transition paragraph below, open and frame the narrative in a way that fits this situation (tone, context, what's going on) while still obeying the numeric rules. Never use the words "voluntary" or "involuntary". Do not default to "voluntary career change"; only imply a chosen pivot if their wording clearly indicates that.`
    : `No welcome_message_answer was provided. Use neutral phrasing such as "career transition" or "career shift". Never use the words "voluntary" or "involuntary".`;

  const p1GapRule = h.hasSurplus
    ? `The user has a MONTHLY SURPLUS (income exceeds expenses). Describe it as a surplus of ${ctx.monthlyGapFmt} that they do NOT need to cover. Do not use the word "gap" for this amount. Say they have room or can save.`
    : h.isBalanced
      ? `The user's income and expenses are balanced (no gap). Say their income covers their expenses; there is no shortfall to cover.`
      : `The user has a monthly gap (expenses exceed income). Describe it as a gap of ${ctx.monthlyGapFmt} that they need to cover. Total shortfall over the career shift is ${ctx.totalShortfallFmt}.`;
  const p1LiquidityRule = h.liquiditySufficient
    ? `Their liquidity (${ctx.liquidityFmt}) is enough to cover the shortfall (or there is no shortfall). Say they have enough accessible short-term liquidity from savings and investment accounts.`
    : `Their liquidity (${ctx.liquidityFmt}) does not fully cover the total shortfall (${ctx.totalShortfallFmt}). Acknowledge the shortfall and their current liquidity; encourage a plan to fill the gap (e.g. part-time work, reducing expenses).`;

  const p2RunwayRule = h.hasSurplus || h.isBalanced
    ? `The user has no income shortfall (surplus or balanced). Do not say they need to "cover" a gap. Say they have sufficient runway at current expenses (or that their income covers expenses during the transition). Still add this advisory: "To protect your cash buffer, consider options like part-time or contract work to help fill the gap and preserve your emergency fund." (Or a variant if they have surplus: encourage preserving the surplus as buffer.)`
    : h.liquiditySufficient
      ? `The user has a gap but liquidity (${ctx.liquidityFmt}) covers the total shortfall (${ctx.totalShortfallFmt}) over ${ctx.durationMonths} months. Say they have enough runway (e.g. roughly X months at current expenses) and a plan to replenish cash after the transition. Add: "To protect your cash buffer, consider options like part-time or contract work to help fill the gap and preserve your emergency fund."`
      : `The user has a gap and liquidity (${ctx.liquidityFmt}) does not fully cover the shortfall. Say how many months of runway they have at current expenses and that they should consider part-time or contract work to preserve their emergency fund. Add: "To protect your cash buffer, consider options like part-time or contract work to help fill the gap and preserve your emergency fund."`;

  const p3DebtRule = h.debtRatioBand === 'high'
    ? `Debt-to-asset ratio is ${ctx.debtToAssetRatio}% (high). Recommend they work to stay below 40% and prioritize paying down debt. Keep: "This shows room to pay down debt or build up investment assets once income stabilizes."`
    : h.debtRatioBand === 'mid'
      ? `Debt-to-asset ratio is ${ctx.debtToAssetRatio}% (moderate). Recommend they stay within or below this range. Keep: "This shows room to pay down debt or build up investment assets once income stabilizes."`
      : h.debtRatioBand === 'low'
        ? `Debt-to-asset ratio is ${ctx.debtToAssetRatio}% (low). Encourage staying low or becoming debt-free. Keep: "This shows room to pay down debt or build up investment assets once income stabilizes."`
        : `Debt-to-asset ratio is 0%. Say they have minimal or no debt. Keep: "This shows room to pay down debt or build up investment assets once income stabilizes."`;
  const p3CreditRule = h.creditCardBand === 'low'
    ? `Credit card debt is ${ctx.creditCardDebtFmt} ($1,000 or less). Congratulate them and advise them to keep up the good work.`
    : `Credit card debt is ${ctx.creditCardDebtFmt} (over $1,000). Include: (1) If their credit card balances carry high interest, look to pay them off immediately. (2) Or consider 0% or low-interest balance transfer or loan options with strong credit, while weighing pay-down versus keeping liquidity during the transition.`;

  const includeDashboardPlaceholders = ctx.includeDashboardPlaceholders === true && ctx.onboardingContext != null;
  const keysLine = includeDashboardPlaceholders
    ? `exactly ${FULL_CHECKUP_PLAN_KEYS.length} keys: ${FULL_CHECKUP_PLAN_KEYS.map((k) => `"${k}"`).join(', ')}. Paragraph keys are strings. FinancialDashboardActionCardDescriptionPlaceholders is a JSON object with exactly seven SHORT string values (see below)—these are the only user-specific fragments inserted into otherwise fixed Forecast dashboard card copy; output plain phrases only (no full sentences), typically amounts or short phrases, no leading/trailing line breaks.`
    : 'exactly three keys: "CareerTransitionAndCashFlowSecondParagraph", "CareerTransitionAndCashFlowThirdParagraph", and "CareerTransitionAndCashFlowFourthParagraph"';

  return `Return one JSON object with ${keysLine}. Use the user's numbers exactly. Say "monthly surplus" when income exceeds expenses. Never say "voluntary" or "involuntary".
    Framing: ${shiftFramingRule}

    CareerTransitionAndCashFlowSecondParagraph: one paragraph. Open with "${ctx.opening}" and weave welcome_message_answer when present. ${p1GapRule} ${p1LiquidityRule} Income ${ctx.monthlyIncomeFmt}, expenses ${ctx.monthlyExpensesFmt}, duration ${ctx.durationMonths} months, shortfall ${ctx.totalShortfallFmt}, liquidity ${ctx.liquidityFmt}.

    CareerTransitionAndCashFlowThirdParagraph: one paragraph on runway and cash buffer. ${p2RunwayRule} Use runway "${ctx.runwayFmt}" exactly. Liquidity ${ctx.liquidityFmt}, gap/surplus ${ctx.monthlyGapFmt}, expenses ${ctx.monthlyExpensesFmt}, duration ${ctx.durationMonths} months.

    CareerTransitionAndCashFlowFourthParagraph: one paragraph. ${p3DebtRule} ${p3CreditRule} Keep this exact sentence: "This shows room to pay down debt or build up investment assets once income stabilizes." Assets ${ctx.totalAssetsFmt}, liabilities ${ctx.totalLiabilitiesFmt}, debt-to-asset ${ctx.debtToAssetRatio}%, credit card ${ctx.creditCardDebtFmt}.
${includeDashboardPlaceholders ? (() => {
      const u = ctx;
      const r = u.retirementContext;
      const careerBlock = [
        'Career transition & cash flow (use these exact values when tailoring):',
        `welcome_message_answer: ${JSON.stringify((u.welcomeMessageAnswer ?? '').toString().trim())}; Monthly income: ${u.monthlyIncomeFmt}, monthly expenses: ${u.monthlyExpensesFmt}, monthly gap/surplus: ${u.monthlyGapFmt}, career shift duration: ${u.durationMonths} months, total shortfall over shift: ${u.totalShortfallFmt}, short-term liquidity: ${u.liquidityFmt}, runway: ${u.runwayFmt}, total assets: ${u.totalAssetsFmt}, total liabilities: ${u.totalLiabilitiesFmt}, debt-to-asset ratio: ${u.debtToAssetRatio}%, credit card debt: ${u.creditCardDebtFmt}, duration display: ${u.durationDisplay ?? 'N/A'}.`
      ].join(' ');
      const retirementBlock = r ? [
        'Freedom, retirement & longevity (use these exact values when tailoring):',
        `Retirement ages: ${r.retirementAgeSummary}; household projection age: ${r.retirementAge}; has spouse: ${r.hasSpouse}, Financial Longevity Score: ${r.longevityScoreDisplayFmt ?? 'N/A'}, projected net worth at retirement: ${r.projectedNetWorthFmt}, projected retirement savings: ${r.projectedSavingsFmt}, annual retirement contribution: ${r.annualContribFmt}, annual income (estimate): ${r.annualIncomeFmt}, years until retirement: ${r.yearsToRetirement}, pension: ${r.hasPension ? r.pensionMonthlyFmt + '/month at age ' + r.pensionAge : 'none'}, Social Security start age: ${r.ssAge}, SS combined monthly: ${r.ssMonthlyFmt}, monthly expenses: ${r.monthlyExpensesFmt}.`
      ].join(' ') : '';
      const userDataBlock = [careerBlock, retirementBlock].filter(Boolean).join(' | ');
      return `
    FinancialDashboardActionCardDescriptionPlaceholders: object with exactly these short fragments (no full sentences):
    monthlyGapPhrase (into "cover the ___ monthly gap"; surplus -> "$0 with a monthly surplus"),
    plannedTransitionPhrase (into "planned ___ transition", e.g. "12-month career"),
    liquidityPhrase (into "Preserve your ___ liquidity"),
    cashCushionPhrase (into "buffer your ___ cash cushion"),
    totalShortfallPhrase (into "cover your ___ total shortfall"; $0 if none),
    longevitySuccessScorePhrase (into "Long-Term Success Score of ___"; score > 80 -> ">80%", score < 40 -> "<40%", else "72%"),
    lifetimeWealthIncreasePhrase (into "a ___ increase in lifetime wealth"; short phrase, no invented precise percent).
    Data: ${userDataBlock}
`;
    })() : ''}

    Return only the JSON object.`;
}

function extractJsonFromChatResponse(raw) {
  if (typeof raw !== 'string' || !raw.trim()) return null;
  let str = raw.trim();
  str = str.replace(/^```(?:json)?\s*\n?/i, '').replace(/\n?```\s*$/, '').trim();
  if (str.startsWith('{')) return str;
  const match = str.match(/\{[\s\S]*\}/);
  return match ? match[0] : null;
}

function planKeyToSnake(planKey) {
  return planKey.replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, '');
}

function normalizeCheckupPlanKeys(plan) {
  if (!plan || typeof plan !== 'object') return plan;
  const result = { ...plan };
  for (const camelKey of FULL_CHECKUP_PLAN_KEYS) {
    const snakeKey = planKeyToSnake(camelKey);
    if (result[camelKey] === undefined && plan[snakeKey] !== undefined) result[camelKey] = plan[snakeKey];
  }
  return result;
}

function getParagraphFromPlan(plan, camelKey) {
  if (!plan || typeof plan !== 'object') return '';
  const v = plan[camelKey] ?? plan[planKeyToSnake(camelKey)];
  return typeof v === 'string' && v.trim() ? v.trim() : '';
}

function normalizeActionCardDescriptionPlaceholdersObject(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const out = {};
  for (const camelKey of ACTION_CARD_DESCRIPTION_PLACEHOLDER_KEYS) {
    const snakeKey = actionCardDescPlaceholderKeyToSnake(camelKey);
    const v = raw[camelKey] ?? raw[snakeKey];
    if (typeof v !== 'string' || !String(v).trim()) return null;
    out[camelKey] = String(v).trim();
  }
  return out;
}

async function fetchFullCheckupPlanFromAdminChat(token, prompt, { useAdminChat = false } = {}) {
  const endpoint = useAdminChat ? config.API_ENDPOINTS.ADMIN_CHAT : config.API_ENDPOINTS.ONBOARDING_AI_CHAT;
  const response = await fetch(`${config.API_BASE_URL}${endpoint}`, {
    method: 'POST',
    headers: getApiHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      token,
      question: prompt,
      json_mode: true,
      prefer_fast: true,
      max_output_tokens: 2048,
    }),
  });
  const data = await response.json();
  const raw = data?.response ?? data?.data?.response ?? data?.answer ?? data?.data?.answer ?? data?.result ?? (data?.data?.result ?? '');
  const str = extractJsonFromChatResponse(raw);
  if (!str) return {};
  try {
    const parsed = JSON.parse(str);
    const payload = {};
    const paragraphKeys = [
      'CareerTransitionAndCashFlowSecondParagraph',
      'CareerTransitionAndCashFlowThirdParagraph',
      'CareerTransitionAndCashFlowFourthParagraph',
    ];
    for (const key of paragraphKeys) {
      const v = parsed[key];
      if (typeof v === 'string' && v.trim()) payload[key] = v.trim();
    }

    const descPlaceholders = normalizeActionCardDescriptionPlaceholdersObject(
      parsed.FinancialDashboardActionCardDescriptionPlaceholders
      ?? parsed.financial_dashboard_action_card_description_placeholders
    );
    if (descPlaceholders) payload.FinancialDashboardActionCardDescriptionPlaceholders = descPlaceholders;
    return payload;
  } catch (_) {
    return {};
  }
}

function buildSnapshotValuesFromProgress(progress) {
  const { assets, debts } = extractAssetsAndDebts(progress) || {};
  if (!assets || !debts) return { netWorth: null, liabilities: null };
  const totalAssets = Object.values(assets).reduce((s, v) => s + (Number(v) || 0), 0);
  const totalDebts = Object.values(debts).reduce((s, v) => s + (Number(v) || 0), 0);
  return {
    netWorth: totalAssets - totalDebts,
    liabilities: totalDebts
  };
}

/** Normalize a projected net worth chart row for display and y-axis bounds (consistent currentDollar fallback). */
function normalizeProjectedNetWorthChartRow(item) {
  if (!item || typeof item !== 'object') {
    return {
      age: 0,
      realEstate: 0,
      otherAssets: 0,
      liabilities: 0,
      currentDollar: 0,
      futureDollar: 0,
      totalStackedValue: 0,
    };
  }
  const realEstate = Number(item.realEstate) || 0;
  const otherAssets = Number(item.otherAssets) || 0;
  const liabilities = item.liabilities !== undefined ? Number(item.liabilities) || 0 : 0;
  const currentDollar = item.currentDollar !== undefined
    ? Number(item.currentDollar) || 0
    : (realEstate + otherAssets - liabilities);
  const futureDollar = item.futureDollar !== undefined ? Number(item.futureDollar) || 0 : currentDollar;
  return {
    age: item.age,
    realEstate: Math.round(realEstate),
    otherAssets: Math.round(otherAssets),
    liabilities: Math.round(liabilities),
    currentDollar: Math.round(currentDollar),
    futureDollar: Math.round(futureDollar),
    totalStackedValue: Math.round(realEstate + otherAssets),
  };
}

function buildProjectionValuesFromFullData(fullData, retirementAgeForLookup) {
  if (!fullData) return null;
  const pnw = fullData.projectedNetWorth || {};
  const atRetirement = retirementAgeForLookup != null
    ? pnw.projectionData?.find((p) => Number(p.age) === Number(retirementAgeForLookup))
    : null;
  return {
    projectedNetWorthAtRetirement: fullData.liquidNetWorthAtRetirement
      ?? (atRetirement?.currentDollar != null ? Number(atRetirement.currentDollar) : null)
      ?? pnw.retirement
      ?? null,
    projectedSavingsAtRetirement: atRetirement != null ? (Number(atRetirement.otherAssets) || 0) : null,
    longevityScore: fullData.retirementSuccess?.percentage ?? null
  };
}

function getCheckupUpdateEndpoint(useAdminEndpointForSummary) {
  return useAdminEndpointForSummary
    ? config.API_ENDPOINTS.ADMIN_ONBOARDING_AI_UPDATE_CHECKUP_AND_ACTION_PLAN
    : config.API_ENDPOINTS.ONBOARDING_AI_UPDATE_CHECKUP_AND_ACTION_PLAN;
}

function buildCheckupPlanOnboardingContext(narrativeData, flowValues, snapshotValues, projectionValues, progress, summaryText) {
  const parts = [];
  parts.push(`Career: ${narrativeData.durationDisplay} shift, target end ${narrativeData.shiftEndYear ?? CHECKUP_NO_DATA}; monthly income ${narrativeData.monthlyIncome != null ? formatCurrency(narrativeData.monthlyIncome) : CHECKUP_NO_DATA}, expenses ${narrativeData.monthlyExpenses != null ? formatCurrency(narrativeData.monthlyExpenses) : CHECKUP_NO_DATA}, gap/surplus ${narrativeData.monthlyGap != null ? formatCurrency(narrativeData.monthlyGap) : CHECKUP_NO_DATA}; short-term liquidity ${narrativeData.shortTermLiquidity != null ? formatCompactCurrencyTwoDecimals(narrativeData.shortTermLiquidity) : CHECKUP_NO_DATA}, runway ${narrativeData.runwayDisplay ?? CHECKUP_NO_DATA}; net worth ${narrativeData.netWorthDisplay}, debt-to-asset ${narrativeData.debtToAssetRatio ?? '0'}%.`);
  parts.push(`Retirement: ${narrativeData.retirementAgeSummary ?? `target age ${narrativeData.retirementAge ?? CHECKUP_NO_DATA}`}; household projection age ${narrativeData.retirementAge ?? CHECKUP_NO_DATA}, longevity score ${narrativeData.longevityScoreDisplay ?? CHECKUP_NO_DATA}, projected net worth at retirement ${narrativeData.projectedNetWorthDisplay ?? CHECKUP_NO_DATA}, annual retirement contribution ${narrativeData.annualContribDisplay ?? CHECKUP_NO_DATA}${narrativeData.hasSpouse ? ', household (with spouse/partner)' : ''}.`);
  if (flowValues?.monthlyGap > 0 && flowValues?.careerShiftDurationMonths) parts.push(`Total shortfall over shift: ${formatCurrency(flowValues.monthlyGap * flowValues.careerShiftDurationMonths)}.`);
  if (snapshotValues?.liabilities != null) parts.push(`Total liabilities: ${formatCompactCurrencyTwoDecimals(snapshotValues.liabilities)}.`);
  const ccDebtVal = progress['owe_credit_cards'] != null ? parseNumericValue(progress['owe_credit_cards'], null) : null;
  if (ccDebtVal != null && ccDebtVal > 0) parts.push(`Credit card debt: ${formatCurrency(ccDebtVal)}.`);
  if (projectionValues?.longevityScore != null) parts.push(`Financial Longevity Score (numeric band): ${projectionValues.longevityScore >= 80 ? 'strong' : projectionValues.longevityScore >= 50 ? 'moderate' : 'room to grow'}.`);
  const welcome = narrativeData?.welcomeMessageAnswer != null ? String(narrativeData.welcomeMessageAnswer).trim() : '';
  if (welcome) parts.push(`welcome_message_answer: ${welcome.slice(0, 350)}${welcome.length > 350 ? '…' : ''}`);
  if (summaryText && typeof summaryText === 'string' && summaryText.trim()) parts.push(`Summary: ${summaryText.trim().slice(0, 400)}${summaryText.length > 400 ? '…' : ''}`);
  return parts.join(' ');
}

function hasCheckupProgress(progress) {
  return progress && typeof progress === 'object' && !Array.isArray(progress) && Object.keys(progress).length > 0;
}

function parseCheckupProgressInt(progress, key, fallback = 0) {
  const v = progress?.[key];
  const n = parseInt(v, 10);
  return Number.isNaN(n) ? fallback : n;
}

function getRetirementAgeDetails(progress) {
  if (!hasCheckupProgress(progress)) {
    return {
      userRetirementAge: null,
      partnerRetirementAge: null,
      householdRetirementAge: null,
      hasSpouse: false,
    };
  }
  const userRetirementAge = parseCheckupProgressInt(progress, CHECKUP_PROGRESS_KEYS.RETIREMENT_AGE, 0) || null;
  const hasSpouse = isMarried(progress[CHECKUP_PROGRESS_KEYS.FAMILY_SITUATION]);
  const partnerRetirementAge = hasSpouse
    ? (parseCheckupProgressInt(progress, CHECKUP_PROGRESS_KEYS.PARTNER_RETIRE, 0) || null)
    : null;
  const householdRetirementAge = hasSpouse && partnerRetirementAge != null
    ? Math.max(userRetirementAge || 0, partnerRetirementAge)
    : userRetirementAge;

  return {
    userRetirementAge,
    partnerRetirementAge,
    householdRetirementAge: householdRetirementAge || null,
    hasSpouse,
  };
}

function getRetirementAgeSummary({
  userRetirementAge,
  partnerRetirementAge,
  householdRetirementAge,
  hasSpouse,
}) {
  if (!hasSpouse) return `You want to retire at age ${userRetirementAge ?? CHECKUP_NO_DATA}`;
  if (userRetirementAge != null && partnerRetirementAge != null) {
    if (userRetirementAge === partnerRetirementAge) {
      return `You and your spouse/partner both want to retire at age ${userRetirementAge}`;
    }
    return `You want to retire at age ${userRetirementAge}, and your spouse/partner plans to retire at age ${partnerRetirementAge}`;
  }
  if (userRetirementAge != null) return `You want to retire at age ${userRetirementAge}`;
  if (partnerRetirementAge != null) return `Your spouse/partner plans to retire at age ${partnerRetirementAge}`;
  return `Retirement target age is ${householdRetirementAge ?? CHECKUP_NO_DATA}`;
}

function getNetWorthBenchmarks(summaryText, netWorth, state, city) {
  let usPercent = null;
  let localPercent = null;

  if (summaryText && typeof summaryText === 'string') {
    const usMatch = summaryText.match(/top\s+(\d{1,3})\s*%\s+of\s+U\.?S\.?\s+households/i);
    if (usMatch) {
      const n = parseInt(usMatch[1], 10);
      if (n >= 1 && n <= 100) usPercent = n;
    } else {
      const genericTop = summaryText.match(/top\s+(\d{1,3})\s*%|(\d{1,3})\s*th\s+percentile/i);
      if (genericTop) {
        const n = parseInt(genericTop[1] || genericTop[2], 10);
        if (n >= 1 && n <= 100) usPercent = n;
      }
    }
    const localMatch = summaryText.match(/top\s+(\d{1,3})\s*%\s*\(\s*locally\s*\)/i);
    if (localMatch) {
      const n = parseInt(localMatch[1], 10);
      if (n >= 1 && n <= 100) localPercent = n;
    }
  }

  if (usPercent == null && netWorth != null && Number.isFinite(Number(netWorth))) {
    usPercent = getTopPercentUS(Number(netWorth));
  }
  if (localPercent == null && netWorth != null && Number.isFinite(Number(netWorth))) {
    localPercent = getTopPercentLocal(Number(netWorth), state, city);
  }

  return {
    usPercent: usPercent ?? 25,
    localPercent,
  };
}

function getSnapshotDescription(summaryText, netWorth, state, city) {
  const { usPercent, localPercent } = getNetWorthBenchmarks(summaryText, netWorth, state, city);

  if (localPercent != null) {
    return `Net Worth top ${usPercent}% (US Households), top ${localPercent}% (locally).`;
  }
  return `Net Worth top ${usPercent}% (US Households)`;
}

function buildCheckupPromptContextFromFinancialData(fullData, onboardingProgress, summaryText = '') {
  const progress = onboardingProgress && typeof onboardingProgress === 'object' && !Array.isArray(onboardingProgress)
    ? onboardingProgress
    : {};
  if (!hasCheckupProgress(progress) && !fullData?.careerShiftRunway) return null;

  const flowValues = fullData?.careerShiftRunway || (hasCheckupProgress(progress) ? getCareerShiftRunwayData(progress) : null);
  const snapshotValues = buildSnapshotValuesFromProgress(progress);
  const retirementAgeDetails = getRetirementAgeDetails(progress);
  const householdRetirementAge = retirementAgeDetails.householdRetirementAge;
  const userRetirementAgeForProjection = retirementAgeDetails.userRetirementAge ?? householdRetirementAge;
  const projectionValues = buildProjectionValuesFromFullData(fullData, userRetirementAgeForProjection);
  const welcomeMessageAnswer =
    progress?.welcome_message_answer ??
    progress?.welcomeMessageAnswer ??
    fullData?.financial_profile?.welcome_message_answer ??
    fullData?.financialProfile?.welcomeMessageAnswer ??
    '';

  const durationMonths = flowValues?.careerShiftDurationMonths ?? parseDurationToMonths(progress);
  const durationDisplay = durationMonths != null ? `${durationMonths}-month` : CHECKUP_NO_DATA;
  const married = retirementAgeDetails.hasSpouse;
  const retirementAge = householdRetirementAge;
  const retirementAgeSummary = getRetirementAgeSummary(retirementAgeDetails);
  const netWorth = snapshotValues?.netWorth ?? null;
  const shiftEndYear = flowValues?.careerShiftDurationMonths != null ? new Date().getFullYear() + Math.ceil(flowValues.careerShiftDurationMonths / 12) : null;
  const longevityScore = projectionValues?.longevityScore ?? null;
  const longevityScoreDisplay = longevityScore != null ? formatLongevityScoreDisplay(longevityScore) : CHECKUP_NO_DATA;
  const pensionPlan = String(progress[CHECKUP_PROGRESS_KEYS.PENSION_PLAN] || '').toLowerCase().includes('yes');
  const pensionMonthly = pensionPlan ? parseNumericValue(progress[CHECKUP_PROGRESS_KEYS.PENSION_MONTHLY], 0) : 0;
  const pensionAge = pensionPlan ? parseCheckupProgressInt(progress, CHECKUP_PROGRESS_KEYS.PENSION_START_AGE, retirementAge ?? 0) : retirementAge;
  const ssUserAge = parseCheckupProgressInt(progress, CHECKUP_PROGRESS_KEYS.SS_BENEFITS_AGE, 0);
  const ssSpouseAge = married ? parseCheckupProgressInt(progress, CHECKUP_PROGRESS_KEYS.PARTNER_SS_AGE, 0) : null;
  const ssUser = getSocialSecurityMonthlyBenefit(ssUserAge) || 0;
  const ssSpouse = ssSpouseAge != null ? (getSocialSecurityMonthlyBenefit(ssSpouseAge) || 0) : 0;
  const ssCombined = Math.round(ssUser + ssSpouse);
  const ssAgeDisplay = ssSpouseAge != null ? Math.max(ssUserAge, ssSpouseAge) : ssUserAge;
  const projectedNetWorth = projectionValues?.projectedNetWorthAtRetirement ?? null;
  const annualContrib = parseNumericValue(progress['total_retirement_account_each_year'], 0);
  const monthlyIncomeAfter = parseNumericValue(progress['monthly_net_income_after_career_shift'], 0);
  const partnerMonthly = married ? parseNumericValue(progress[CHECKUP_PROGRESS_KEYS.MONTHLY_INCOME_PARTNER], 0) : 0;
  const annualIncomeEstimate = (monthlyIncomeAfter + partnerMonthly) * 12;
  const yob = progress[CHECKUP_PROGRESS_KEYS.YOUR_YOB];
  const currentAge = yob ? calculateAgeFromBirthYear(String(yob).trim()) : null;
  const primaryRetirementAge = retirementAgeDetails.userRetirementAge ?? retirementAge;
  const yearsToRetirement = (currentAge != null && primaryRetirementAge != null) ? Math.max(0, primaryRetirementAge - currentAge) : null;

  const narrativeData = {
    durationMonths,
    durationDisplay,
    retirementAge,
    userRetirementAge: retirementAgeDetails.userRetirementAge,
    partnerRetirementAge: retirementAgeDetails.partnerRetirementAge,
    retirementAgeSummary,
    hasSpouse: married,
    netWorthDisplay: netWorth != null ? formatCompactCurrencyTwoDecimals(netWorth) : CHECKUP_NO_DATA,
    shiftEndYear,
    shortTermLiquidity: flowValues?.accessibleShortTermLiquidity ?? null,
    monthlyGap: flowValues?.monthlyGap ?? null,
    monthlyExpenses: flowValues?.currentMonthlyExpenses ?? null,
    monthlyIncome: flowValues?.currentMonthlyIncome ?? null,
    runwayDisplay: flowValues?.runwayDisplayValue === 'Fully Covered' ? 'Fully Covered' : (flowValues?.runwayValue ?? CHECKUP_NO_DATA),
    debtToAssetRatio: (snapshotValues?.netWorth ?? 0) + (snapshotValues?.liabilities ?? 0) > 0 ? (((snapshotValues?.liabilities ?? 0) / ((snapshotValues?.netWorth ?? 0) + (snapshotValues?.liabilities ?? 0))) * 100).toFixed(1) : '0',
    longevityScoreDisplay,
    welcomeMessageAnswer,
    pensionMonthlyDisplay: pensionMonthly > 0 ? formatCompactCurrencyTwoDecimals(pensionMonthly) : null,
    pensionAge,
    ssMonthlyDisplay: ssCombined > 0 ? formatCompactCurrencyTwoDecimals(ssCombined) : null,
    ssAge: ssAgeDisplay,
    projectedNetWorthDisplay: projectedNetWorth != null ? formatCompactCurrencyTwoDecimals(projectedNetWorth) : CHECKUP_NO_DATA,
    annualContribDisplay: annualContrib != null ? formatCompactCurrencyTwoDecimals(annualContrib) : CHECKUP_NO_DATA
  };

  const monthlyGapVal = flowValues?.monthlyGap ?? 0;
  const durationMonthsVal = flowValues?.careerShiftDurationMonths || 24;
  const totalShortfall = monthlyGapVal > 0 ? monthlyGapVal * durationMonthsVal : 0;
  const liquidity = flowValues?.accessibleShortTermLiquidity ?? 0;
  const runwayFmt = flowValues?.runwayDisplayValue === 'Fully Covered'
    ? 'Fully Covered'
    : `${flowValues?.runwayValue ?? CHECKUP_NO_DATA} months`;
  const opening = monthlyGapVal <= 0 || liquidity >= totalShortfall ? 'Congratulations' : 'Do not worry, we are there to help you with your career shift transition';
  const totalAssetsVal = (snapshotValues?.netWorth ?? 0) + (snapshotValues?.liabilities ?? 0);
  const creditCardDebt = parseNumericValue(progress['owe_credit_cards'], 0);

  const secondParagraphContext = {
    opening,
    welcomeMessageAnswer,
    monthlyIncome: flowValues?.currentMonthlyIncome ?? 0,
    monthlyExpenses: flowValues?.currentMonthlyExpenses ?? 0,
    monthlyGap: monthlyGapVal,
    durationMonths: durationMonthsVal,
    totalShortfall,
    liquidity,
    runwayFmt,
    durationDisplay: narrativeData.durationDisplay,
    monthlyIncomeFmt: formatCurrency(flowValues?.currentMonthlyIncome ?? 0),
    monthlyExpensesFmt: formatCurrency(flowValues?.currentMonthlyExpenses ?? 0),
    monthlyGapFmt: formatCurrency(Math.abs(monthlyGapVal)),
    totalShortfallFmt: formatCurrency(totalShortfall),
    liquidityFmt: formatCurrency(liquidity),
    totalAssetsFmt: formatCurrency(totalAssetsVal),
    totalLiabilitiesFmt: formatCurrency(snapshotValues?.liabilities ?? 0),
    debtToAssetRatio: narrativeData.debtToAssetRatio ?? '0',
    creditCardDebt,
    creditCardDebtFmt: formatCurrency(creditCardDebt)
  };

  const retirementParagraphContext = projectionValues && narrativeData.retirementAge != null
    ? {
      retirementAge: narrativeData.retirementAge,
      userRetirementAge: narrativeData.userRetirementAge,
      partnerRetirementAge: narrativeData.partnerRetirementAge,
      retirementAgeSummary: narrativeData.retirementAgeSummary,
      hasSpouse: narrativeData.hasSpouse,
      hasPension: pensionMonthly > 0 && (narrativeData.pensionAge != null && narrativeData.pensionAge > 0),
      pensionMonthly,
      pensionMonthlyFmt: pensionMonthly > 0 ? formatCurrency(pensionMonthly) : formatCurrency(0),
      pensionAge: narrativeData.pensionAge ?? retirementAge,
      ssAge: narrativeData.ssAge ?? 67,
      ssMonthlyFmt: narrativeData.ssMonthlyDisplay ?? formatCurrency(0),
      projectedNetWorthFmt: narrativeData.projectedNetWorthDisplay ?? CHECKUP_NO_DATA,
      projectedSavingsFmt: projectionValues.projectedSavingsAtRetirement != null ? formatCompactCurrencyTwoDecimals(projectionValues.projectedSavingsAtRetirement) : CHECKUP_NO_DATA,
      longevityScore: projectionValues.longevityScore ?? null,
      longevityScoreDisplayFmt: projectionValues.longevityScore != null ? formatLongevityScoreDisplay(projectionValues.longevityScore) : CHECKUP_NO_DATA,
      monthlyExpensesFmt: narrativeData.monthlyExpenses != null ? formatCurrency(narrativeData.monthlyExpenses) : CHECKUP_NO_DATA,
      annualRetirementContrib: annualContrib,
      annualContribFmt: annualContrib > 0 ? formatCurrency(annualContrib) : formatCurrency(0),
      annualIncomeFmt: annualIncomeEstimate > 0 ? formatCurrency(annualIncomeEstimate) : CHECKUP_NO_DATA,
      yearsToRetirement: yearsToRetirement != null ? yearsToRetirement : CHECKUP_NO_DATA
    }
    : null;

  const checkupPlanOnboardingContext = buildCheckupPlanOnboardingContext(
    narrativeData,
    flowValues,
    snapshotValues,
    projectionValues,
    progress,
    summaryText
  );

  return { secondParagraphContext, retirementParagraphContext, checkupPlanOnboardingContext };
}

export async function runCheckupPlanGenerationAndSave({
  fullData,
  onboardingProgress,
  summaryText,
  token,
  summaryUsername,
  useAdminEndpointForSummary,
  signal
}) {
  const context = buildCheckupPromptContextFromFinancialData(fullData, onboardingProgress, summaryText);
  if (!context?.secondParagraphContext) return null;

  const promptContext = {
    ...context.secondParagraphContext,
    retirementContext: context.retirementParagraphContext || undefined,
    includeDashboardPlaceholders: true,
    onboardingContext: context.checkupPlanOnboardingContext || ''
  };
  const prompt = buildCareerTransitionParagraphsPrompt(promptContext);
  const payload = await fetchFullCheckupPlanFromAdminChat(token, prompt, { useAdminChat: useAdminEndpointForSummary });
  if (signal?.aborted || !payload || Object.keys(payload).length === 0) return null;

  const updateEndpoint = getCheckupUpdateEndpoint(useAdminEndpointForSummary);
  const body = useAdminEndpointForSummary
    ? { token, username: summaryUsername, onboarding_ai_checkup_and_action_plan: payload }
    : { token, onboarding_ai_checkup_and_action_plan: payload };

  // Persist after the forecast can render. The save is a small write; the model call is the wait.
  void fetch(`${config.API_BASE_URL}${updateEndpoint}`, {
    method: 'PUT',
    headers: getApiHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
    signal
  }).then(async (updateRes) => {
    if (!updateRes.ok) {
      const updateData = await updateRes.json().catch(() => ({}));
      logger.warn('Failed to save checkup and plan:', updateData?.message || updateRes.statusText);
    }
  }).catch((err) => {
    if (err?.name !== 'AbortError') logger.warn('Failed to save checkup and plan:', err);
  });

  return payload;
}

function CheckupSparkleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 3.2l1.15 5.05L18.2 9.4 13.15 11.2 12 16.4l-1.15-5.2L5.8 9.4l5.05-1.15L12 3.2z"
        fill="currentColor"
      />
      <path d="M18.4 14.2l.55 2.05 2.05.55-2.05.55-.55 2.05-.55-2.05-2.05-.55 2.05-.55.55-2.05z" fill="currentColor" />
    </svg>
  );
}

function CheckupPersonIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="8" r="3.25" stroke="currentColor" strokeWidth="1.7" />
      <path
        d="M5.5 18.5c.9-3.1 3.1-4.7 6.5-4.7s5.6 1.6 6.5 4.7"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CheckupCtaArrow({ className }) {
  return (
    <svg className={className} viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M3 8h9.2M8.8 4.2 13.5 8l-4.7 3.8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function formatCheckupUpdatedLabel(now = new Date()) {
  const month = now.toLocaleString('en-US', { month: 'long' });
  return `Updated ${month} ${now.getFullYear()}`;
}

function CheckupDiagnosticFooter({
  onClose,
  onStartMove,
  hideExpertCta = false,
  title = 'Turn this checkup into progress.',
  subtitle = 'Start your next best money move with quick, practical Richuels.',
}) {
  return (
    <ForecastProgressFooter
      className={styles.checkupDiagnosticFooter}
      title={title}
      subtitle={subtitle}
      onStartMove={onStartMove}
      onClose={onClose}
      hideCta={hideExpertCta}
      ariaLabel={title}
    />
  );
}

const CheckupSummarySection = memo(function CheckupSummarySection({
  onboardingProgress,
  financialData,
  checkupAndActionPlan,
  displayUserName = 'User',
  summaryText = '',
  wmtRef = null,
  view = 'banner',
  onOpenDiagnostic = null,
  onBack = null,
  onStartMove = null,
  isAdmin = false,
}) {
  const progress = useMemo(() => {
    if (!onboardingProgress || typeof onboardingProgress !== 'object' || Array.isArray(onboardingProgress)) return {};
    return normalizeOnboardingProgressKeys(onboardingProgress);
  }, [onboardingProgress]);

  const normalizedPlan = useMemo(
    () => normalizeCheckupPlanKeys(normalizeCheckupAndActionPlan(checkupAndActionPlan)),
    [checkupAndActionPlan]
  );

  const headerLines = useMemo(() => {
    const yob = progress[CHECKUP_PROGRESS_KEYS.YOUR_YOB];
    const partnerYob = progress[CHECKUP_PROGRESS_KEYS.PARTNER_YOB];
    const familySituation = progress[CHECKUP_PROGRESS_KEYS.FAMILY_SITUATION];
    const childrenNum = progress[CHECKUP_PROGRESS_KEYS.CHILDREN_NUMBER];
    const married = isMarried(familySituation);
    const age = yob ? calculateAgeFromBirthYear(String(yob).trim()) : null;
    const spouseAge = married && partnerYob ? calculateAgeFromBirthYear(String(partnerYob).trim()) : null;
    let line1 = displayUserName;
    if (age != null) line1 += ` (${age})`;
    if (spouseAge != null) line1 += ` & Spouse (${spouseAge})`;
    let line2 = familySituation ? String(familySituation) : '';
    const num = childrenNum != null && childrenNum !== '' ? parseInt(childrenNum, 10) : NaN;
    if (!Number.isNaN(num)) {
      line2 += num === 0 ? (line2 ? ' with no children' : 'No children') : (line2 ? ` with ${num} ${num === 1 ? 'child' : 'children'}` : `${num} ${num === 1 ? 'child' : 'children'}`);
    }
    const city = progress[CHECKUP_PROGRESS_KEYS.CITY];
    const state = progress[CHECKUP_PROGRESS_KEYS.STATE];
    const location = [city, state].filter(Boolean).join(', ') || '';
    return { line1, line2: line2.trim(), location };
  }, [displayUserName, progress]);

  const snapshotValues = useMemo(() => buildSnapshotValuesFromProgress(progress), [progress]);

  const snapshotDescription = useMemo(
    () => getSnapshotDescription(
      summaryText || '',
      snapshotValues?.netWorth,
      typeof progress[CHECKUP_PROGRESS_KEYS.STATE] === 'string' ? String(progress[CHECKUP_PROGRESS_KEYS.STATE]).trim() : '',
      typeof progress[CHECKUP_PROGRESS_KEYS.CITY] === 'string' ? String(progress[CHECKUP_PROGRESS_KEYS.CITY]).trim() : ''
    ),
    [summaryText, snapshotValues?.netWorth, progress]
  );

  const flowValues = useMemo(() => (hasCheckupProgress(progress) ? getCareerShiftRunwayData(progress) : null), [progress]);

  const hasFundsToCover = useMemo(() => {
    if (!flowValues) return null;
    return (
      flowValues.runwayDisplayValue === 'Fully Covered' ||
      (parseFloat(flowValues.runwayValue) >= (flowValues.careerShiftDurationMonths || 0))
    );
  }, [flowValues]);

  const projectionValues = useMemo(() => {
    if (!financialData) return null;
    const retirementAgeDetails = getRetirementAgeDetails(progress);
    const lookupAge = retirementAgeDetails.userRetirementAge ?? retirementAgeDetails.householdRetirementAge;
    return buildProjectionValuesFromFullData(financialData, lookupAge);
  }, [financialData, progress]);

  const narrativeData = useMemo(() => {
    const progressOk = hasCheckupProgress(progress);
    const welcomeMessageAnswer =
      progress?.welcome_message_answer ??
      progress?.welcomeMessageAnswer ??
      '';
    const durationMonths = progressOk ? (flowValues?.careerShiftDurationMonths ?? parseDurationToMonths(progress)) : null;
    const durationDisplay = durationMonths != null ? `${durationMonths}-month` : CHECKUP_NO_DATA;
    const retirementAgeDetails = getRetirementAgeDetails(progress);
    const married = retirementAgeDetails.hasSpouse;
    const baseIncome = progressOk ? parseNumericValue(progress['monthly_net_income_during_career_shift'], 0) : 0;
    const partnerIncome = married ? parseNumericValue(progress[CHECKUP_PROGRESS_KEYS.MONTHLY_INCOME_PARTNER], 0) : 0;
    const monthlyIncomeDuringShift = progressOk ? (flowValues?.currentMonthlyIncome ?? baseIncome + partnerIncome) : null;
    const retirementAge = retirementAgeDetails.householdRetirementAge;
    const retirementAgeSummary = getRetirementAgeSummary(retirementAgeDetails);
    const netWorth = snapshotValues?.netWorth ?? null;
    const shiftEndYear = (progressOk && flowValues?.careerShiftDurationMonths != null)
      ? new Date().getFullYear() + Math.ceil(flowValues.careerShiftDurationMonths / 12)
      : null;
    const shortTermLiquidity = flowValues?.accessibleShortTermLiquidity ?? null;
    const monthlyGap = flowValues?.monthlyGap ?? null;
    const monthlyExpenses = flowValues?.currentMonthlyExpenses ?? null;
    const monthlyIncome = flowValues?.currentMonthlyIncome ?? null;
    const parsedRunway = flowValues?.runwayValue != null ? parseFloat(flowValues.runwayValue) : null;
    const runwayMonthsNum = flowValues?.runwayValue === 'Fully Covered' ? null : (parsedRunway != null && Number.isFinite(parsedRunway) ? parsedRunway : null);
    const totalAssets = (snapshotValues?.netWorth ?? 0) + (snapshotValues?.liabilities ?? 0);
    const debtToAssetRatio = totalAssets > 0 ? (((snapshotValues?.liabilities ?? 0) / totalAssets) * 100).toFixed(1) : '0';
    const longevityScore = projectionValues?.longevityScore ?? null;
    const longevityScoreDisplay = longevityScore != null ? formatLongevityScoreDisplay(longevityScore) : CHECKUP_NO_DATA;
    const pensionPlan = progressOk && String(progress[CHECKUP_PROGRESS_KEYS.PENSION_PLAN] || '').toLowerCase().includes('yes');
    const pensionMonthly = pensionPlan ? parseNumericValue(progress[CHECKUP_PROGRESS_KEYS.PENSION_MONTHLY], 0) : 0;
    const pensionAge = pensionPlan ? parseCheckupProgressInt(progress, CHECKUP_PROGRESS_KEYS.PENSION_START_AGE, retirementAge ?? 0) : retirementAge;
    const ssUserAge = parseCheckupProgressInt(progress, CHECKUP_PROGRESS_KEYS.SS_BENEFITS_AGE, 0);
    const ssSpouseAge = married ? parseCheckupProgressInt(progress, CHECKUP_PROGRESS_KEYS.PARTNER_SS_AGE, 0) : null;
    const ssUser = getSocialSecurityMonthlyBenefit(ssUserAge) || 0;
    const ssSpouse = ssSpouseAge != null ? (getSocialSecurityMonthlyBenefit(ssSpouseAge) || 0) : 0;
    const ssCombined = Math.round(ssUser + ssSpouse);
    const ssAgeDisplay = ssSpouseAge != null ? Math.max(ssUserAge, ssSpouseAge) : ssUserAge;
    const projectedNetWorth = projectionValues?.projectedNetWorthAtRetirement ?? null;
    const annualContrib = progressOk ? parseNumericValue(progress['total_retirement_account_each_year'], 0) : null;
    const yob = progress[CHECKUP_PROGRESS_KEYS.YOUR_YOB];
    const currentAge = yob ? calculateAgeFromBirthYear(String(yob).trim()) : null;
    const primaryRetirementAge = retirementAgeDetails.userRetirementAge ?? retirementAge;
    const yearsToRetirement = (currentAge != null && primaryRetirementAge != null) ? Math.max(0, primaryRetirementAge - currentAge) : null;
    const lifeExpectancy = FINANCIAL_CONSTANTS.LIFE_EXPECTANCY ?? 95;
    const yearsInRetirement = (retirementAge != null && Number.isFinite(retirementAge))
      ? Math.max(0, lifeExpectancy - retirementAge)
      : null;
    const healthcareCost = progressOk ? (married ? HEALTHCARE_COSTS.MARRIED : HEALTHCARE_COSTS.SINGLE) : null;
    const lifetimeNeeds = (yearsInRetirement != null && monthlyExpenses != null && healthcareCost != null)
      ? monthlyExpenses * 12 * yearsInRetirement + healthcareCost
      : null;
    return {
      welcomeMessageAnswer,
      durationMonths,
      durationDisplay,
      monthlyIncomeDuringShift,
      retirementAge,
      userRetirementAge: retirementAgeDetails.userRetirementAge,
      partnerRetirementAge: retirementAgeDetails.partnerRetirementAge,
      retirementAgeSummary,
      hasSpouse: married,
      netWorthDisplay: netWorth != null ? formatCompactCurrencyTwoDecimals(netWorth) : CHECKUP_NO_DATA,
      shiftEndYear,
      shortTermLiquidity,
      monthlyGap,
      monthlyExpenses,
      monthlyIncome,
      runwayMonths: runwayMonthsNum,
      runwayDisplay: flowValues?.runwayDisplayValue === 'Fully Covered' ? 'Fully Covered' : (runwayMonthsNum != null ? runwayMonthsNum : CHECKUP_NO_DATA),
      debtToAssetRatio,
      longevityScoreDisplay,
      pensionMonthlyDisplay: pensionMonthly > 0 ? formatCompactCurrencyTwoDecimals(pensionMonthly) : null,
      pensionAge,
      ssMonthlyDisplay: ssCombined > 0 ? formatCompactCurrencyTwoDecimals(ssCombined) : null,
      ssAge: ssAgeDisplay,
      projectedNetWorthDisplay: projectedNetWorth != null ? formatCompactCurrencyTwoDecimals(projectedNetWorth) : CHECKUP_NO_DATA,
      annualContribDisplay: annualContrib != null ? formatCompactCurrencyTwoDecimals(annualContrib) : CHECKUP_NO_DATA,
      annualContribRaw: annualContrib != null ? Number(annualContrib) : null,
      yearsToRetirement: yearsToRetirement != null ? yearsToRetirement : CHECKUP_NO_DATA,
      healthcareCost,
      healthcareCostDisplay: healthcareCost != null ? healthcareCost.toLocaleString('en-US') : CHECKUP_NO_DATA,
      lifetimeNeeds,
      lifetimeNeedsDisplay: lifetimeNeeds != null ? formatCompactCurrencyTwoDecimals(lifetimeNeeds) : CHECKUP_NO_DATA,
    };
  }, [progress, snapshotValues, flowValues, projectionValues]);

  const careerTransitionBanner = useMemo(() => {
    const durationDisplay = narrativeData?.durationDisplay ?? '';
    const durationPhrase = durationDisplay && durationDisplay !== CHECKUP_NO_DATA ? `${durationDisplay} ` : '';
    if (!hasFundsToCover) {
      return {
        strongPill: 'Not Strong',
        bannerSub: `You are not in a good position to cover expenses during your ${durationPhrase}career shift. Take some immediate measures to save for your short-term runway.`,
      };
    }
    return {
      strongPill: 'Strong',
      bannerSub: `Good position to cover expenses during your planned ${durationPhrase}transition. Take steps to replenish emergency savings after.`,
    };
  }, [hasFundsToCover, narrativeData?.durationDisplay]);

  const snapshotBanner = useMemo(() => {
    if (!hasFundsToCover) {
      return {
        strongPill: 'Not Strong',
        bannerSub: snapshotDescription
          ? `${snapshotDescription} Your position may need attention—focus on building liquidity and runway.`
          : 'Your current position may need attention. Focus on building liquidity and managing debt.',
      };
    }
    return {
      strongPill: 'Strong',
      bannerSub: snapshotDescription || 'Your current net worth and liquidity position support your financial goals.',
    };
  }, [hasFundsToCover, snapshotDescription]);

  const checkupParagraphs = useMemo(() => ({
    careerSecond: getParagraphFromPlan(normalizedPlan, 'CareerTransitionAndCashFlowSecondParagraph'),
    careerThird: getParagraphFromPlan(normalizedPlan, 'CareerTransitionAndCashFlowThirdParagraph'),
    careerFourth: getParagraphFromPlan(normalizedPlan, 'CareerTransitionAndCashFlowFourthParagraph'),
  }), [normalizedPlan]);

  const freedomRetirementStatus = useMemo(() => {
    const age = narrativeData?.retirementAge ?? CHECKUP_NO_DATA;
    const ageSummary = narrativeData?.retirementAgeSummary ?? `You are currently targeting retirement at ${age}`;
    return getFreedomRetirementStatus(projectionValues?.longevityScore, ageSummary);
  }, [narrativeData?.retirementAge, narrativeData?.retirementAgeSummary, projectionValues?.longevityScore]);

  const overallStatus = useMemo(() => (
    hasFundsToCover
      ? { label: 'Strong', tone: 'strong' }
      : { label: 'Not Strong', tone: 'nudge' }
  ), [hasFundsToCover]);

  const bannerUserLine = useMemo(() => {
    const ageMatch = headerLines.line1.match(/^(.*?)(?:\s+\((\d+)\))?$/);
    const name = (ageMatch?.[1] || headerLines.line1 || displayUserName).trim();
    const age = ageMatch?.[2];
    const detailParts = [headerLines.line2, headerLines.location].filter(Boolean);
    const detail = detailParts.join(' · ');
    if (age && detail) return `${name} (${age}, ${detail})`;
    if (age) return `${name} (${age})`;
    if (detail) return `${name} · ${detail}`;
    return name;
  }, [displayUserName, headerLines]);

  const diagnosticUserLine = useMemo(
    () => [headerLines.line1, headerLines.line2, headerLines.location].filter(Boolean).join(' • '),
    [headerLines]
  );

  const bannerTeaser = useMemo(() => {
    const income = narrativeData?.monthlyIncome;
    const expenses = narrativeData?.monthlyExpenses;
    const gap = narrativeData?.monthlyGap;
    const incomeFmt = income != null ? formatCurrency(income) : CHECKUP_NO_DATA;
    const expensesFmt = expenses != null ? formatCurrency(expenses) : CHECKUP_NO_DATA;

    if (income == null && expenses == null) {
      return 'Open your diagnostic to review cash flow, snapshot, and longevity insights.';
    }
    if (gap != null && gap < 0) {
      return (
        <>
          Your monthly income of {incomeFmt} exceeds expenses of {expensesFmt}, yielding a calm{' '}
          <strong className={styles.checkupBannerHighlight}>{formatCurrency(Math.abs(gap))} surplus</strong>
          {hasFundsToCover ? ' and full transition safety.' : '.'}
        </>
      );
    }
    if (gap != null && gap > 0) {
      return (
        <>
          Your monthly expenses of {expensesFmt} exceed income of {incomeFmt}, leaving a{' '}
          <strong className={styles.checkupBannerHighlight}>{formatCurrency(gap)} gap</strong> to cover during this transition.
        </>
      );
    }
    return `Your monthly income of ${incomeFmt} currently matches expenses of ${expensesFmt}.`;
  }, [hasFundsToCover, narrativeData?.monthlyExpenses, narrativeData?.monthlyGap, narrativeData?.monthlyIncome]);

  const snapshotMetric = useMemo(() => {
    const { usPercent } = getNetWorthBenchmarks(
      summaryText || '',
      snapshotValues?.netWorth,
      typeof progress[CHECKUP_PROGRESS_KEYS.STATE] === 'string' ? String(progress[CHECKUP_PROGRESS_KEYS.STATE]).trim() : '',
      typeof progress[CHECKUP_PROGRESS_KEYS.CITY] === 'string' ? String(progress[CHECKUP_PROGRESS_KEYS.CITY]).trim() : ''
    );
    if (usPercent > 0) return `Top ${usPercent}% Net Worth`;
    return narrativeData?.netWorthDisplay && narrativeData.netWorthDisplay !== CHECKUP_NO_DATA
      ? `${narrativeData.netWorthDisplay} Net Worth`
      : 'Current Net Worth';
  }, [narrativeData?.netWorthDisplay, progress, snapshotValues?.netWorth, summaryText]);

  const cashFlowMetric = useMemo(() => {
    const gap = narrativeData?.monthlyGap;
    if (gap != null && gap < 0) return `${formatCurrency(Math.abs(gap))} Monthly Surplus`;
    if (gap != null && gap > 0) return `${formatCurrency(gap)} Monthly Gap`;
    return 'Balanced Cash Flow';
  }, [narrativeData?.monthlyGap]);

  const longevityMetric = useMemo(() => {
    const age = narrativeData?.userRetirementAge ?? narrativeData?.retirementAge;
    return age != null && age !== CHECKUP_NO_DATA ? `Target Age ${age}` : 'Target Age';
  }, [narrativeData?.retirementAge, narrativeData?.userRetirementAge]);

  const whatsHappeningBody = checkupParagraphs.careerSecond || (
    <>
      You are navigating a career transition and want to plan a <strong>{narrativeData.durationDisplay}</strong> duration for the shift. During that time, your household will have a monthly net income of <strong>{narrativeData.monthlyIncomeDuringShift != null ? formatCurrency(narrativeData.monthlyIncomeDuringShift) : CHECKUP_NO_DATA}</strong>. {narrativeData.retirementAgeSummary}.
    </>
  );

  const pillarCards = [
    {
      key: 'snapshot',
      kicker: 'Snapshot',
      statusLabel: snapshotBanner.strongPill,
      tone: snapshotBanner.strongPill === 'Not Strong' ? 'nudge' : 'strong',
      title: 'Current Financial Snapshot',
      metric: snapshotMetric,
      description: snapshotBanner.bannerSub,
    },
    {
      key: 'cashflow',
      kicker: 'Cash Flow',
      statusLabel: hasFundsToCover ? 'Good Position' : 'Needs Attention',
      tone: hasFundsToCover ? 'strong' : 'nudge',
      title: 'Career Transition & Cash Flow',
      metric: cashFlowMetric,
      description: careerTransitionBanner.bannerSub,
    },
    {
      key: 'longevity',
      kicker: 'Longevity',
      statusLabel: freedomRetirementStatus.strongPill,
      tone: freedomRetirementStatus.isStrong ? 'strong' : 'nudge',
      title: 'Freedom, Retirement & Longevity',
      metric: longevityMetric,
      description: freedomRetirementStatus.bannerSub,
    },
  ];

  const statusPillClass = overallStatus.tone === 'nudge'
    ? styles.checkupStatusChipNudge
    : styles.checkupStatusChipStrong;

  if (view === 'diagnostic') {
    return (
      <div className={styles.checkupDiagnosticPage}>
        <section className={styles.checkupDiagnosticCard} aria-label="Your Financial Checkup Summary">
          <header className={styles.checkupDiagnosticHeader}>
            <div className={styles.checkupDiagnosticEyebrowRow}>
              <span className={styles.checkupDiagnosticEyebrow}>
                <span className={styles.checkupDiagnosticEyebrowIconWrap} aria-hidden="true">
                  <img
                    src={financialCheckupIcon}
                    alt=""
                    className={styles.checkupDiagnosticEyebrowIcon}
                    decoding="async"
                  />
                </span>
                Mindful Diagnostic
              </span>
              <span className={statusPillClass}>
                <span className={styles.checkupStatusChipDot} aria-hidden="true" />
                Status: {overallStatus.label}
              </span>
              <span className={styles.checkupDiagnosticUpdated}>{formatCheckupUpdatedLabel()}</span>
            </div>
            <h2 className={styles.checkupDiagnosticTitle}>Your Financial Checkup Summary</h2>
            <p className={styles.checkupDiagnosticUserLine}>
              <span className={styles.checkupDiagnosticUserIcon} aria-hidden="true">
                <CheckupPersonIcon />
              </span>
              <span>{diagnosticUserLine}</span>
            </p>
          </header>

          <section className={styles.checkupWhatsHappening} aria-label="What's happening">
            <h3 className={styles.checkupWhatsHappeningTitle}>
              <span className={styles.checkupWhatsHappeningIcon} aria-hidden="true">
                <CheckupSparkleIcon />
              </span>
              What&apos;s Happening
            </h3>
            <div className={styles.checkupSummaryContent}>
              <p>{whatsHappeningBody}</p>
            </div>
          </section>

          <section className={styles.checkupPillarsSection} aria-label="Key health pillars and diagnostics">
            <h3 className={styles.checkupPillarsHeading}>Key Health Pillars &amp; Diagnostics</h3>
            <div className={styles.checkupPillarsGrid}>
              {pillarCards.map((card) => (
                <article key={card.key} className={styles.checkupPillarCard}>
                  <div className={styles.checkupPillarKickerRow}>
                    <span className={styles.checkupPillarKicker}>{card.kicker}</span>
                    <span className={card.tone === 'nudge' ? styles.checkupStatusChipNudge : styles.checkupStatusChipStrong}>
                      {card.statusLabel}
                    </span>
                  </div>
                  <h4 className={styles.checkupPillarTitle}>{card.title}</h4>
                  <p className={styles.checkupPillarMetric}>{card.metric}</p>
                  {card.description ? (
                    <p className={styles.checkupPillarDescription}>{card.description}</p>
                  ) : null}
                </article>
              ))}
            </div>
          </section>

          <CheckupDiagnosticFooter
            onClose={onBack}
            onStartMove={onStartMove}
            hideExpertCta={isAdmin}
          />
        </section>
      </div>
    );
  }

  return (
    <section
      ref={wmtRef}
      className={`${styles.checkupSectionTitleStrip} ${styles.checkupDiagnosticBanner}`}
      aria-label="Financial Checkup"
    >
      <span className={styles.checkupBannerIconWrap} aria-hidden="true">
        <img
          src={financialCheckupIcon}
          alt=""
          className={styles.checkupBannerIcon}
          decoding="async"
        />
      </span>
      <div className={styles.checkupBannerCopy}>
        <div className={styles.checkupBannerEyebrowRow}>
          <p className={styles.checkupBannerEyebrow}>Financial Checkup</p>
          <span className={statusPillClass}>
            <span className={styles.checkupStatusChipDot} aria-hidden="true" />
            Status: {overallStatus.label}
          </span>
        </div>
        <p className={styles.checkupBannerUser}>{bannerUserLine}</p>
        <p className={styles.checkupBannerTeaser}>{bannerTeaser}</p>
      </div>
      <button
        type="button"
        className={styles.checkupBannerCta}
        onClick={onOpenDiagnostic}
      >
        Open Diagnostic
        <CheckupCtaArrow className={styles.checkupBannerCtaArrow} />
      </button>
    </section>
  );
});

const CASH_FLOW_SAVINGS_RATE_TARGET = 0.25;

function roundToNiceDollars(value) {
  if (!(value > 0)) return 0;
  if (value >= 1000) return Math.round(value / 50) * 50;
  return Math.round(value / 10) * 10;
}

function percentChange(current, baseline) {
  if (!(baseline > 0)) return null;
  return Math.round(((current - baseline) / baseline) * 100);
}

function sparkHeights(trend, intensity) {
  const t = Math.max(0, Math.min(1, Number(intensity) || 0));
  if (trend === 'up') {
    const peak = 14 + Math.round(t * 12);
    return [10, 14, 13, 18, peak];
  }
  const low = 22 - Math.round(t * 9);
  return [26, 22, 20, 16, Math.max(8, low)];
}

function isRunwayFullyCovered(runway) {
  return runway?.runwayDisplayValue === FINANCIAL_CONSTANTS.FULLY_COVERED
    || runway?.runwayValue === FINANCIAL_CONSTANTS.FULLY_COVERED;
}

function buildCashFlowSnapshot(progress, runway) {
  if (!runway) return null;

  const normalized = normalizeOnboardingProgressKeys(progress);
  const income = Math.max(0, Number(runway.currentMonthlyIncome) || 0);
  const expenses = Math.max(0, Number(runway.currentMonthlyExpenses) || 0);
  const surplus = income - expenses;
  const married = isMarried(normalized?.[CHECKUP_PROGRESS_KEYS.FAMILY_SITUATION]);
  const postShiftSelf = parseNumericValue(normalized?.monthly_net_income_after_career_shift, 0) || 0;
  const partnerIncome = married
    ? (parseNumericValue(normalized?.[CHECKUP_PROGRESS_KEYS.MONTHLY_INCOME_PARTNER], 0) || 0)
    : 0;
  const postShiftIncome = postShiftSelf + partnerIncome;
  const vsPostShift = percentChange(income, postShiftIncome);

  let incomeChip;
  if (vsPostShift != null && vsPostShift !== 0) {
    incomeChip = vsPostShift > 0
      ? { tone: 'up', text: `↑ ${vsPostShift}% vs post-shift plan` }
      : { tone: 'down', text: `↓ ${Math.abs(vsPostShift)}% during shift` };
  } else if (postShiftIncome > 0) {
    incomeChip = { tone: 'up', text: 'Aligned with post-shift plan' };
  } else if (expenses > 0 && income > 0) {
    const cover = Math.round((income / expenses) * 100);
    incomeChip = {
      tone: cover >= 100 ? 'up' : 'down',
      text: `Covers ${cover}% of expenses`,
    };
  } else {
    incomeChip = { tone: 'neutral', text: 'From your financial profile' };
  }

  let expensesChip;
  if (income > 0) {
    const ratio = Math.round((expenses / income) * 100);
    expensesChip = expenses <= income
      ? { tone: 'down', text: `Uses ${ratio}% of income` }
      : { tone: 'up', text: `↑ ${ratio - 100}% over income` };
  } else {
    expensesChip = { tone: 'neutral', text: 'From your financial profile' };
  }

  const savingsTarget = roundToNiceDollars(income * CASH_FLOW_SAVINGS_RATE_TARGET);
  const fullyCovered = isRunwayFullyCovered(runway);
  let savingsStatus;
  if ((surplus >= savingsTarget && surplus > 0) || (surplus > 0 && fullyCovered)) {
    savingsStatus = { tone: 'good', text: "You're on track" };
  } else if (surplus > 0) {
    savingsStatus = { tone: 'good', text: 'Positive surplus' };
  } else if (surplus === 0) {
    savingsStatus = { tone: 'neutral', text: 'Break even' };
  } else if (fullyCovered) {
    savingsStatus = { tone: 'warn', text: 'Gap, runway covered' };
  } else {
    savingsStatus = { tone: 'warn', text: 'Monthly gap' };
  }

  const expenseControl = income > 0 ? Math.max(0, Math.min(1, 1 - (expenses / income))) : 0.3;

  return {
    income,
    expenses,
    surplus,
    incomeChip,
    expensesChip,
    savingsStatus,
    savingsTarget,
    incomeBars: sparkHeights('up', surplus > 0 ? 0.75 : 0.35),
    expenseBars: sparkHeights('down', expenseControl),
  };
}

function cashFlowChipClass(cardKey, tone) {
  if (cardKey === 'savings') {
    if (tone === 'good') return styles.cashFlowStatusGood;
    if (tone === 'warn') return styles.cashFlowStatusWarn;
    return styles.cashFlowStatusNeutral;
  }
  if (tone === 'up') return styles.cashFlowChipUp;
  if (tone === 'down') return styles.cashFlowChipDown;
  return styles.cashFlowChipNeutral;
}

function MiniSparkBars({ heights, fill }) {
  const maxH = 28;
  const barW = 5.2;
  const gap = 3.6;
  const width = heights.length * (barW + gap) - gap;
  return (
    <svg
      width={width}
      height={maxH}
      viewBox={`0 0 ${width} ${maxH}`}
      className={styles.cashFlowSpark}
      aria-hidden="true"
    >
              {heights.map((h, i) => {
                const height = Math.max(4, h);
                return (
                  <rect
                    key={i}
                    x={i * (barW + gap)}
                    y={maxH - height}
                    width={barW}
                    height={height}
                    rx="1.8"
                    fill={fill}
                  />
                );
              })}
    </svg>
  );
}

const CashFlowSnapshotSection = memo(function CashFlowSnapshotSection({ progress, runway, wmtRef = null }) {
  const snapshot = useMemo(() => {
    const flow = runway || (progress ? getCareerShiftRunwayData(progress) : null);
    return buildCashFlowSnapshot(progress, flow);
  }, [progress, runway]);
  if (!snapshot) return null;

  const cards = [
    {
      key: 'income',
      label: 'Monthly Income',
      value: formatCurrency(snapshot.income),
      icon: cashFlowIncomeIcon,
      chip: snapshot.incomeChip,
      spark: { heights: snapshot.incomeBars, fill: '#5ecfbc' },
    },
    {
      key: 'expenses',
      label: 'Monthly Expenses',
      value: formatCurrency(snapshot.expenses),
      icon: cashFlowExpensesIcon,
      chip: snapshot.expensesChip,
      spark: { heights: snapshot.expenseBars, fill: '#7eb6e6' },
    },
    {
      key: 'savings',
      label: 'Savings / Surplus',
      value: formatCurrency(snapshot.surplus),
      icon: cashFlowSavingsIcon,
      chip: snapshot.savingsStatus,
      target: snapshot.savingsTarget > 0 ? `Target: ${formatCurrency(snapshot.savingsTarget)}` : null,
    },
  ];

  return (
    <section ref={wmtRef} className={styles.cashFlowSnapshot} aria-label="Monthly cash flow snapshot">
      <div className={styles.cashFlowSnapshotGrid}>
        {cards.map((card) => (
          <article key={card.key} className={styles.cashFlowCard} aria-label={`${card.label} ${card.value}. ${card.chip.text}`}>
            <div className={styles.cashFlowCardMain}>
              <div className={styles.cashFlowCardCopy}>
                <span className={styles.cashFlowCardLabel}>{card.label}</span>
                <p className={styles.cashFlowCardValue}>{card.value}</p>
              </div>
              <span className={styles.cashFlowCardIcon} aria-hidden="true">
                <img src={card.icon} alt="" decoding="async" />
              </span>
            </div>
            <div className={styles.cashFlowCardFooter}>
              <span className={`${styles.cashFlowChip} ${cashFlowChipClass(card.key, card.chip.tone)}`}>
                {card.key === 'savings' ? (
                  <span className={styles.cashFlowStatusDot} aria-hidden="true" />
                ) : null}
                {card.chip.text}
              </span>
              {card.spark ? (
                <MiniSparkBars heights={card.spark.heights} fill={card.spark.fill} />
              ) : card.target ? (
                <span className={styles.cashFlowTarget}>{card.target}</span>
              ) : null}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
});

function formatMonthlyGapDisplay(gap) {
  if (gap > 0) return formatCurrency(-gap);
  if (gap < 0) return `+${formatCurrency(-gap)}`;
  return formatCurrency(0);
}

function parseRunwayMonthsValue(runway) {
  if (isRunwayFullyCovered(runway)) return Infinity;
  const parsed = parseFloat(runway?.runwayValue);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
}

function formatRunwayHeadlineMonths(months) {
  const rounded = Math.round(months * 10) / 10;
  return rounded % 1 === 0 ? String(Math.round(rounded)) : rounded.toFixed(1);
}

function buildRunwayTimeline(endMonths) {
  const max = Math.max(3, Math.ceil(Number(endMonths) || 0));
  const ticks = [0];
  for (let month = 3; month < max; month += 3) ticks.push(month);
  const last = ticks[ticks.length - 1];
  if (max - last <= 1) ticks[ticks.length - 1] = max;
  else if (last !== max) ticks.push(max);
  return { max, ticks };
}

function getNearestRunwayTick(months, ticks, max) {
  const clamped = Math.max(0, Math.min(max, months));
  return ticks.reduce((best, tick) => (
    Math.abs(tick - clamped) < Math.abs(best - clamped) ? tick : best
  ));
}

function buildFinancialRunwayViewModel(runway) {
  if (!runway) return null;

  const fullyCovered = isRunwayFullyCovered(runway);
  const durationMonths = Math.max(0, Number(runway.careerShiftDurationMonths) || 0);
  const runwayMonths = parseRunwayMonthsValue(runway);
  const coversPlanned = fullyCovered
    || (Number.isFinite(runwayMonths) && durationMonths > 0 && runwayMonths >= durationMonths);
  const headlineMonths = fullyCovered
    ? (durationMonths > 0 ? durationMonths : 12)
    : (Number.isFinite(runwayMonths) ? runwayMonths : 0);
  const scaleEnd = Math.max(durationMonths, Number.isFinite(headlineMonths) ? headlineMonths : 0);
  const timeline = buildRunwayTimeline(scaleEnd > 0 ? scaleEnd : 12);
  const clampedHeadline = Math.max(0, Math.min(timeline.max, headlineMonths));
  const nearestTick = getNearestRunwayTick(headlineMonths, timeline.ticks, timeline.max);

  return {
    fullyCovered,
    coversPlanned,
    durationMonths,
    runwayMonths,
    headlineMonths,
    headlineNumber: formatRunwayHeadlineMonths(headlineMonths),
    timelineMax: timeline.max,
    timelineTicks: timeline.ticks,
    fillPercent: (clampedHeadline / timeline.max) * 100,
    activeTick: Math.abs(nearestTick - clampedHeadline) <= 0.75 ? nearestTick : null,
    statusLabel: coversPlanned ? 'Fully Covered' : 'Needs Attention',
    statusTone: coversPlanned ? 'strong' : 'nudge',
    bannerMessage: coversPlanned
      ? "You're fully covered for your planned career transition."
      : 'Your current runway is shorter than your planned career transition.',
    runwayMetric: fullyCovered
      ? FINANCIAL_CONSTANTS.FULLY_COVERED
      : formatRunwayMonthsDisplay(runway.runwayValue),
  };
}

function FinancialRunwayCheckIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M3.2 8.2 6.4 11.4 12.8 4.8"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function FinancialRunwayCoveredMark() {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" aria-hidden="true">
      <circle cx="11" cy="11" r="11" fill="#E5F6EF" />
      <circle cx="11" cy="11" r="8" fill="#1F9D6A" />
      <path
        d="M7.15 11.15 9.55 13.5 14.85 8.35"
        fill="none"
        stroke="#fff"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const ChartCardHeader = memo(function ChartCardHeader({
  title,
  subtitle,
  filterValue,
  filterOptions,
  filterAriaLabel,
  onFilterChange,
}) {
  return (
    <div className={styles.chartCardHeader}>
      <div className={styles.chartCardHeading}>
        <h3 className={styles.sectionTitle}>{title}</h3>
        <p className={styles.sectionSubtitle}>{subtitle}</p>
      </div>
      <div className={styles.chartCardFilter}>
        <select
          className={styles.chartCardSelect}
          value={filterValue}
          aria-label={filterAriaLabel}
          onChange={(event) => onFilterChange(event.target.value)}
        >
          {filterOptions.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>
    </div>
  );
});

const TRANSITION_INCOME_KEYS = ['work', 'severance', 'unemployment', 'temporary', 'passive'];
const TRANSITION_EXPENSES_COLOR = CHART_CONFIG.colors.expensesLine;
const TRANSITION_SERIES = [
  { key: 'work', label: 'Work Income', color: CHART_CONFIG.colors.workIncome },
  { key: 'severance', label: 'Severance', color: CHART_CONFIG.colors.socialSecurity },
  { key: 'unemployment', label: 'Unemployment', color: CHART_CONFIG.colors.collegeSavingsDrawdown },
  { key: 'temporary', label: 'Temporary Income', color: CHART_CONFIG.colors.savingsDrawdown },
  { key: 'passive', label: 'Passive Income', color: CHART_CONFIG.colors.pension },
];
const TRANSITION_STACK_ORDER = TRANSITION_SERIES.map((series) => series.key);
const TRANSITION_SERIES_BY_KEY = Object.fromEntries(TRANSITION_SERIES.map((series) => [series.key, series]));

const TRANSITION_CHART = {
  width: 760,
  height: 286,
  pad: { top: 14, right: 10, bottom: 42, left: 58 },
  yMin: 0,
};

function roundUpTransitionAxis(peak) {
  const value = Math.max(0, Number(peak) || 0);
  if (value === 0) return { yMax: 1000, yTicks: [1000, 750, 500, 250, 0] };
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const ceilings = [1, 2, 2.5, 4, 5, 10].map((factor) => factor * magnitude);
  const yMax = ceilings.find((ceiling) => ceiling >= value) ?? magnitude * 10;
  const step = yMax / 4;
  return {
    yMax,
    yTicks: [4, 3, 2, 1, 0].map((count) => count * step),
  };
}


function formatTransitionAmount(value) {
  const rounded = Math.round(Number(value) || 0);
  const abs = Math.abs(rounded).toLocaleString('en-US');
  return rounded < 0 ? `-$${abs}` : `$${abs}`;
}

function roundedTopBarPath(x, y, width, height, radius) {
  const r = Math.max(0, Math.min(radius, width / 2, height));
  if (r <= 0) return `M ${x} ${y + height} H ${x + width} V ${y} H ${x} Z`;
  return [
    `M ${x} ${y + height}`,
    `V ${y + r}`,
    `Q ${x} ${y} ${x + r} ${y}`,
    `H ${x + width - r}`,
    `Q ${x + width} ${y} ${x + width} ${y + r}`,
    `V ${y + height}`,
    'Z',
  ].join(' ');
}

function TransitionChartIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <rect x="1.6" y="10.1" width="3.5" height="5.1" rx="0.8" fill="currentColor" opacity="0.38" />
      <rect x="1.6" y="6.7" width="3.5" height="3" rx="0.8" fill="currentColor" />
      <rect x="7.25" y="8.3" width="3.5" height="6.9" rx="0.8" fill="currentColor" opacity="0.38" />
      <rect x="7.25" y="3.4" width="3.5" height="4.4" rx="0.8" fill="currentColor" />
      <rect x="12.9" y="9.2" width="3.5" height="6" rx="0.8" fill="currentColor" opacity="0.38" />
      <rect x="12.9" y="5.5" width="3.5" height="3.2" rx="0.8" fill="currentColor" />
    </svg>
  );
}

function TransitionCalendarIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="1.9" y="2.8" width="12.2" height="11.2" rx="2" stroke="currentColor" strokeWidth="1.4" />
      <path d="M1.9 6.2h12.2" stroke="currentColor" strokeWidth="1.4" />
      <path d="M5.1 1.6v2.3M10.9 1.6v2.3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="5.4" cy="9.15" r="0.7" fill="currentColor" />
      <circle cx="8" cy="9.15" r="0.7" fill="currentColor" />
      <circle cx="10.6" cy="9.15" r="0.7" fill="currentColor" />
    </svg>
  );
}

function TransitionBulbIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M9 18.2h6M9.8 21h4.4M12 3.2a6.1 6.1 0 0 0-3.3 11.2c.5.4.8 1 .8 1.6v.4h5v-.4c0-.6.3-1.2.8-1.6A6.1 6.1 0 0 0 12 3.2Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TransitionInsightMark() {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" aria-hidden="true">
      <circle cx="14" cy="14" r="14" fill="#FFF6DE" />
      <path
        d="M14 6.4a4.7 4.7 0 0 0-2.55 8.55c.38.3.6.74.6 1.2v.45h3.9v-.45c0-.46.22-.9.6-1.2A4.7 4.7 0 0 0 14 6.4Z"
        fill="#F5B400"
      />
      <path d="M11.7 18.1h4.6M12.2 19.9h3.6" stroke="#C48900" strokeWidth="1.25" strokeLinecap="round" />
      <path d="M20.2 8.2l.45 1.15 1.15.45-1.15.45-.45 1.15-.45-1.15-1.15-.45 1.15-.45.45-1.15Z" fill="#F5B400" />
    </svg>
  );
}

function InsightCheckIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" aria-hidden="true">
      <circle cx="14" cy="14" r="14" fill="#DDF6EA" />
      <circle cx="14" cy="14" r="9" fill="#12A36A" />
      <path d="M9.65 14.2 12.4 16.9 18.4 11" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function InsightWarnIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" aria-hidden="true">
      <circle cx="14" cy="14" r="14" fill="#FFF1D6" />
      <path d="M14 6.15 22.15 20.7H5.85L14 6.15Z" fill="#F5A018" />
      <path d="M14 11.15v3.85" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" />
      <circle cx="14" cy="17.45" r="0.95" fill="#fff" />
    </svg>
  );
}

function InsightExtendIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" aria-hidden="true">
      <circle cx="14" cy="14" r="14" fill="#E4EEFF" />
      <circle cx="14" cy="14" r="9" fill="#3B82F6" />
      <path d="M10.15 17.55 17.55 10.2M13.15 10.2h4.4v4.4" fill="none" stroke="#fff" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function InsightAlertIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" aria-hidden="true">
      <circle cx="14" cy="14" r="14" fill="#FDE8EC" />
      <circle cx="14" cy="14" r="9" fill="#E11D48" />
      <path d="M14 9.4v5.1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="14" cy="17.7" r="1" fill="#fff" />
    </svg>
  );
}

function LongevityInfoIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="6.2" stroke="currentColor" strokeWidth="1.3" />
      <path d="M8 7.15V11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="8" cy="5.15" r="0.7" fill="currentColor" />
    </svg>
  );
}

const TRANSITION_FILTER_ALL = 'all';
const TRANSITION_FILTER_EXPENSES = 'expenses';

const TransitionCashFlowSnapshot = memo(function TransitionCashFlowSnapshot({
  wmtRef = null,
  cashFlowRows = [],
}) {
  const [activeIndex, setActiveIndex] = useState(null);
  const [seriesFilter, setSeriesFilter] = useState(TRANSITION_FILTER_ALL);
  const rows = cashFlowRows;
  const { height, pad, yMin } = TRANSITION_CHART;
  const width = Math.max(
    TRANSITION_CHART.width,
    pad.left + pad.right + Math.max(rows.length, 1) * 46,
  );
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;
  const slot = rows.length > 0 ? plotW / rows.length : plotW;
  const barWidth = Math.min(22, slot * 0.62);

  const showAllSeries = seriesFilter === TRANSITION_FILTER_ALL;
  const showExpensesLine = showAllSeries || seriesFilter === TRANSITION_FILTER_EXPENSES;
  const visibleBarKeys = showAllSeries
    ? TRANSITION_STACK_ORDER
    : seriesFilter === TRANSITION_FILTER_EXPENSES
      ? []
      : TRANSITION_STACK_ORDER.filter((key) => key === seriesFilter);
  const visibleLegendSeries = TRANSITION_SERIES.filter((series) => (
    showAllSeries || seriesFilter === series.key
  ));
  const chartPeak = rows.reduce((max, row) => {
    const barTotal = visibleBarKeys.reduce((sum, key) => sum + (Number(row[key]) || 0), 0);
    const lineValue = showExpensesLine ? Math.max(0, Number(row.expenses) || 0) : 0;
    return Math.max(max, barTotal, lineValue);
  }, 0);
  const { yMax, yTicks } = roundUpTransitionAxis(chartPeak);
  const yPos = (value) => pad.top + ((yMax - value) / (yMax - yMin)) * plotH;
  const activeRow = activeIndex == null || activeIndex < 0 || activeIndex >= rows.length
    ? null
    : rows[activeIndex];
  const rangeLabel = rows.length > 0
    ? `Monthly (${rows.length} ${rows.length === 1 ? 'month' : 'months'})`
    : 'Monthly';
  const tooltipAlign = activeIndex == null
    ? ''
    : activeIndex <= 1
      ? styles.transitionTooltipAlignStart
      : activeIndex >= rows.length - 3
        ? styles.transitionTooltipAlignEnd
        : '';

  return (
    <section ref={wmtRef} className={styles.transitionCard} aria-label="Transition Income Cash Flow Snapshot">
      <header className={styles.transitionCardHeader}>
        <div className={styles.transitionHeading}>
          <span className={styles.transitionTitleIcon} aria-hidden="true">
            <TransitionChartIcon />
          </span>
          <div>
            <h4 className={styles.transitionTitle}>Transition Income Cash Flow Snapshot</h4>
            <p className={styles.transitionSubtitle}>
              Projected income and expenses during your career transition period.
            </p>
          </div>
        </div>
        <div className={styles.transitionRangeStack}>
          <p className={styles.transitionRange}>
            <span className={styles.transitionRangeIcon} aria-hidden="true">
              <TransitionCalendarIcon />
            </span>
            {rangeLabel}
          </p>
          <select
            className={styles.chartCardSelect}
            value={seriesFilter}
            aria-label="Filter transition income series"
            onChange={(event) => {
              setSeriesFilter(event.target.value);
              setActiveIndex(null);
            }}
          >
            <option value={TRANSITION_FILTER_ALL}>All</option>
            {TRANSITION_SERIES.map((series) => (
              <option key={series.key} value={series.key}>{series.label}</option>
            ))}
            <option value={TRANSITION_FILTER_EXPENSES}>Expenses</option>
          </select>
        </div>
      </header>

      {rows.length === 0 ? (
        <p className={styles.transitionSubtitle}>
          Set a transition duration to plot income and expenses month by month.
        </p>
      ) : (
      <div className={styles.transitionChartScroll}>
        <div
          className={styles.transitionChartFrame}
          style={width > TRANSITION_CHART.width ? { minWidth: `${(width / TRANSITION_CHART.width) * 40}rem` } : undefined}
          onPointerLeave={(event) => {
            if (event.pointerType === 'touch') return;
            setActiveIndex(null);
          }}
        >
          <svg
            className={styles.transitionChartSvg}
            viewBox={`0 0 ${width} ${height}`}
            role="img"
            aria-label={`Monthly income and expenses across a ${rows.length} month transition.`}
          >
            {yTicks.map((tick) => {
              const y = yPos(tick);
              return (
                <g key={tick}>
                  <line
                    x1={pad.left}
                    x2={width - pad.right}
                    y1={y}
                    y2={y}
                    className={tick === 0 ? styles.transitionZeroLine : styles.transitionGridLine}
                  />
                  <text x={pad.left - 8} y={y + 4} className={styles.transitionAxisLabel} textAnchor="end">
                    {formatTransitionAmount(tick)}
                  </text>
                </g>
              );
            })}

            {activeIndex != null && (
              <rect
                x={pad.left + activeIndex * slot + 2}
                y={pad.top}
                width={slot - 4}
                height={plotH}
                rx="8"
                className={styles.transitionHoverColumn}
              />
            )}

            {rows.map((row, index) => {
              const x = pad.left + index * slot + (slot - barWidth) / 2;
              let cursor = 0;
              const segments = visibleBarKeys.map((key) => {
                const value = Number(row[key]) || 0;
                if (value <= 0) return null;
                const top = yPos(cursor + value);
                const bottom = yPos(cursor);
                cursor += value;
                return { key, top, height: bottom - top };
              }).filter(Boolean);

              return (
                <g key={`${row.label}-${row.year}-${index}`}>
                  {segments.map((segment, segmentIndex) => {
                    const color = TRANSITION_SERIES_BY_KEY[segment.key].color;
                    const isTop = segmentIndex === segments.length - 1;
                    if (isTop && segment.height > 4) {
                      return (
                        <path
                          key={segment.key}
                          d={roundedTopBarPath(x, segment.top, barWidth, segment.height, 3)}
                          fill={color}
                        />
                      );
                    }
                    return (
                      <rect
                        key={segment.key}
                        x={x}
                        y={segment.top}
                        width={barWidth}
                        height={Math.max(segment.height, 0)}
                        fill={color}
                      />
                    );
                  })}
                  <text
                    x={pad.left + index * slot + slot / 2}
                    y={height - 24}
                    className={styles.transitionMonthLabel}
                    textAnchor="middle"
                  >
                    {row.label}
                  </text>
                  <text
                    x={pad.left + index * slot + slot / 2}
                    y={height - 11}
                    className={styles.transitionYearLabel}
                    textAnchor="middle"
                  >
                    {row.year}
                  </text>
                </g>
              );
            })}

            {showExpensesLine && (
              <polyline
                points={rows.map((row, index) => {
                  const x = pad.left + index * slot + slot / 2;
                  return `${x},${yPos(row.expenses)}`;
                }).join(' ')}
                className={styles.transitionNetLine}
              />
            )}
            {showExpensesLine && rows.map((row, index) => {
              const x = pad.left + index * slot + slot / 2;
              return (
                <circle
                  key={`expenses-${row.label}-${row.year}`}
                  cx={x}
                  cy={yPos(row.expenses)}
                  r="3.4"
                  className={styles.transitionNetDot}
                />
              );
            })}

            {rows.map((row, index) => (
              <rect
                key={`hit-${row.label}-${row.year}-${index}`}
                x={pad.left + index * slot}
                y={pad.top}
                width={slot}
                height={plotH}
                fill="transparent"
                onPointerEnter={(event) => {
                  if (event.pointerType === 'touch') return;
                  setActiveIndex(index);
                }}
                onPointerUp={(event) => {
                  if (event.pointerType !== 'touch') return;
                  setActiveIndex((current) => (current === index ? null : index));
                }}
              />
            ))}
          </svg>

          {activeRow && (
            <div
              className={`${styles.transitionTooltip} ${tooltipAlign}`}
              style={{ left: `${((pad.left + activeIndex * slot + slot / 2) / width) * 100}%` }}
            >
              <p className={styles.transitionTooltipTitle}>{activeRow.label} {activeRow.year}</p>
              <div className={styles.transitionTipRow}>
                <span className={styles.transitionTipLabel}>
                  <span className={styles.transitionTipDot} style={{ background: CHART_CONFIG.colors.workIncome }} />
                  Total Income
                </span>
                <span>{formatTransitionAmount(TRANSITION_INCOME_KEYS.reduce((sum, key) => sum + activeRow[key], 0))}</span>
              </div>
              <div className={styles.transitionTipRow}>
                <span className={styles.transitionTipLabel}>
                  <span className={styles.transitionTipDot} style={{ background: TRANSITION_EXPENSES_COLOR }} />
                  Total Expenses
                </span>
                <span>{formatTransitionAmount(activeRow.expenses)}</span>
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      <ul className={styles.transitionLegend}>
        {visibleLegendSeries.map((series) => (
          <li key={series.key} className={styles.transitionLegendItem}>
            <span className={styles.transitionLegendSwatch} style={{ background: series.color }} />
            {series.label}
          </li>
        ))}
        {showExpensesLine && (
          <li className={styles.transitionLegendItem}>
            <span className={styles.transitionLegendLine} aria-hidden="true" />
            Expenses
          </li>
        )}
      </ul>

      <div className={styles.transitionInsight}>
        <span className={styles.transitionInsightIcon} aria-hidden="true">
          <TransitionInsightMark />
        </span>
        <p>{buildTransitionInsightCopy(rows)}</p>
      </div>
    </section>
  );
});

const FinancialRunwayCard = memo(function FinancialRunwayCard({
  runway,
  wmtRef,
  transitionRef = null,
  transitionRows = [],
  onViewDetails,
}) {
  const view = useMemo(() => buildFinancialRunwayViewModel(runway), [runway]);
  if (!view) return null;

  const statusChipClass = view.statusTone === 'nudge'
    ? styles.financialRunwayStatusNudge
    : styles.financialRunwayStatusCovered;

  return (
    <section
      ref={wmtRef}
      className={`${styles.financialRunwayCard} ${styles.largeCard}`}
      aria-label="Your Financial Runway"
    >
      <header className={styles.financialRunwayHeader}>
        <div className={styles.financialRunwayHeading}>
          <h3 className={`${styles.sectionTitle} ${styles.financialRunwayTitle}`}>
            <span className={styles.financialRunwayTitleDot} aria-hidden="true" />
            Your Financial Runway
          </h3>
          <p className={`${styles.sectionSubtitle} ${styles.financialRunwaySubtitle}`}>
            Based on current income, expenses and short-term liquidity.
          </p>
        </div>
        <div className={styles.financialRunwayHeadline}>
          <p className={styles.financialRunwayHeadlineValue}>
            <span className={styles.financialRunwayHeadlineNumber}>{view.headlineNumber}</span>
            {' '}
            <span className={styles.financialRunwayHeadlineUnit}>Months</span>
          </p>
          <span className={statusChipClass}>
            {view.coversPlanned ? (
              <span className={styles.financialRunwayStatusIcon} aria-hidden="true">
                <FinancialRunwayCheckIcon />
              </span>
            ) : (
              <span className={styles.checkupStatusChipDot} aria-hidden="true" />
            )}
            {view.statusLabel}
          </span>
        </div>
      </header>

      <div className={styles.financialRunwayTimeline} aria-hidden="true">
        <div className={styles.financialRunwayTrack}>
          <div
            className={styles.financialRunwayTrackFill}
            style={{ width: `${view.fillPercent}%` }}
          />
          <span
            className={styles.financialRunwayKnob}
            style={{ left: `${view.fillPercent}%` }}
          />
        </div>
        <div className={styles.financialRunwayTicks}>
          {view.timelineTicks.map((tick) => (
            <span
              key={tick}
              className={`${styles.financialRunwayTick}${tick === 0 ? '' : ` ${styles.financialRunwayTickLabeled}`}${view.activeTick === tick ? ` ${styles.financialRunwayTickActive}` : ''}`}
              style={{ left: `${(tick / view.timelineMax) * 100}%` }}
            >
              {tick === 0 ? '0' : tick}
              {tick !== 0 && <span className={styles.financialRunwayTickUnit}> months</span>}
            </span>
          ))}
        </div>
      </div>

      <TransitionCashFlowSnapshot wmtRef={transitionRef} cashFlowRows={transitionRows} />

      <div className={styles.financialRunwayBanner}>
        <div className={styles.financialRunwayBannerCopy}>
          <span className={styles.financialRunwayBannerIcon} aria-hidden="true">
            <FinancialRunwayCoveredMark />
          </span>
          <p className={styles.financialRunwayBannerText}>{view.bannerMessage}</p>
        </div>
        <button
          type="button"
          className={styles.financialRunwayDetailsBtn}
          onClick={onViewDetails}
        >
          View Details
          <CheckupCtaArrow className={styles.financialRunwayDetailsArrow} />
        </button>
      </div>
    </section>
  );
});

function buildTransitionInsightCopy(rows) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return 'Set a transition duration to see how income and expenses move month by month.';
  }
  const dip = findCashFlowDip(rows);
  if (!dip) return 'Income is projected to cover expenses across your transition.';
  const when = dip.startMonth === dip.endMonth
    ? `month ${dip.startMonth}`
    : `months ${dip.startMonth}–${dip.endMonth}`;
  const boost = formatInsightDollars(suggestTemporaryIncome(dip.depth));
  return `Your income dips in ${when}. Adding about ${boost}/month of temporary income could keep cash flow positive.`;
}

function findCashFlowDip(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  let start = -1;
  let best = null;

  const consider = (from, to) => {
    if (from < 0 || to < from) return;
    const slice = rows.slice(from, to + 1);
    const depth = Math.max(...slice.map((row) => Math.max(0, -(Number(row.net) || 0))));
    const length = to - from + 1;
    if (!best || length > best.length || (length === best.length && depth > best.depth)) {
      best = { startMonth: from + 1, endMonth: to + 1, length, depth };
    }
  };

  rows.forEach((row, index) => {
    if ((Number(row.net) || 0) < 0) {
      if (start < 0) start = index;
      return;
    }
    consider(start, index - 1);
    start = -1;
  });
  consider(start, rows.length - 1);
  return best;
}

function formatInsightDollars(amount) {
  return `$${Math.round(amount).toLocaleString('en-US')}`;
}

function suggestTemporaryIncome(shortfall) {
  const cushioned = Math.max(Number(shortfall) || 0, 1000);
  return Math.ceil(cushioned / 500) * 500;
}

function buildExtendInsight({ runway, dip }) {
  const gap = Math.max(0, Number(runway?.monthlyGap) || 0);
  const liquidity = Math.max(0, Number(runway?.accessibleShortTermLiquidity) || 0);

  if (!dip && gap <= 0) {
    return {
      key: 'extend',
      tone: 'extend',
      title: 'Keep a buffer',
      body: 'A little extra income now can protect your runway if plans shift.',
    };
  }

  const boost = suggestTemporaryIncome(dip?.depth || 0);
  const money = formatInsightDollars(boost);

  if (gap > 0 && boost >= gap) {
    return {
      key: 'extend',
      tone: 'extend',
      title: 'Extend your runway',
      body: `Adding ${money}/month of temporary income could close your monthly gap.`,
    };
  }

  if (gap > 0 && liquidity > 0 && boost < gap) {
    const extra = (liquidity / (gap - boost)) - (liquidity / gap);
    if (Number.isFinite(extra) && extra >= 1) {
      return {
        key: 'extend',
        tone: 'extend',
        title: 'Extend your runway',
        body: `Adding ${money}/month of temporary income could extend your runway by ${Math.floor(extra)}+ months.`,
      };
    }
  }

  const stretch = dip ? `${dip.length}+ months` : 'a few extra months';
  return {
    key: 'extend',
    tone: 'extend',
    title: 'Extend your runway',
    body: `Adding ${money}/month of temporary income could extend your runway by ${stretch}.`,
  };
}

function buildKeyInsights({ runwayMonthsLabel, runwayCovered, runway, cashFlowRows }) {
  const monthsLabel = runwayMonthsLabel || '0';
  const dip = findCashFlowDip(cashFlowRows);
  const dipTitle = !dip
    ? 'Cash flow stays positive'
    : dip.startMonth === dip.endMonth
      ? `Income dip in month ${dip.startMonth}`
      : `Income dip in months ${dip.startMonth}–${dip.endMonth}`;

  return [
    runwayCovered
      ? {
        key: 'runway',
        tone: 'check',
        title: 'Runway looks strong',
        body: `You're projected to cover ${monthsLabel} months based on current assumptions.`,
      }
      : {
        key: 'runway',
        tone: 'alert',
        title: 'Runway is tight',
        body: `You're projected to cover ${monthsLabel} months, short of your planned transition.`,
      },
    dip
      ? {
        key: 'dip',
        tone: 'warn',
        title: dipTitle,
        body: 'Your cash flow may turn negative without additional income.',
      }
      : {
        key: 'dip',
        tone: 'check',
        title: dipTitle,
        body: 'Income is projected to cover expenses across your transition.',
      },
    buildExtendInsight({ runway, dip }),
  ];
}

function KeyInsightIcon({ tone }) {
  if (tone === 'check') return <InsightCheckIcon />;
  if (tone === 'extend') return <InsightExtendIcon />;
  if (tone === 'alert') return <InsightAlertIcon />;
  return <InsightWarnIcon />;
}

const KeyInsightsCard = memo(function KeyInsightsCard({
  runway,
  runwayMonthsLabel,
  runwayCovered,
  cashFlowRows = [],
  onSeeRecommendations,
  wmtRef = null,
}) {
  const insights = buildKeyInsights({
    runwayMonthsLabel,
    runwayCovered,
    runway,
    cashFlowRows,
  }).slice(0, 3);

  return (
    <section ref={wmtRef} className={styles.keyInsightsCard} aria-label="Key Insights">
      <h3 className={styles.keyInsightsTitle}>
        <span className={styles.keyInsightsTitleIcon} aria-hidden="true">
          <TransitionBulbIcon />
        </span>
        Key Insights
      </h3>
      <ul className={styles.keyInsightsList}>
        {insights.map((insight) => (
          <li key={insight.key} className={styles.keyInsightItem}>
            <span className={styles.keyInsightIcon} aria-hidden="true">
              <KeyInsightIcon tone={insight.tone} />
            </span>
            <div>
              <h4 className={styles.keyInsightHeading}>{insight.title}</h4>
              <p className={styles.keyInsightBody}>{insight.body}</p>
            </div>
          </li>
        ))}
      </ul>
      <button type="button" className={styles.keyInsightsButton} onClick={onSeeRecommendations}>
        See Recommendations
        <CheckupCtaArrow className={styles.financialRunwayDetailsArrow} />
      </button>
    </section>
  );
});

const FinancialRunwayDetailsPage = memo(function FinancialRunwayDetailsPage({
  runway,
  isAdmin,
  onBack,
  onStartMove,
}) {
  const view = useMemo(() => buildFinancialRunwayViewModel(runway), [runway]);
  if (!view || !runway) return null;

  const statusPillClass = view.statusTone === 'nudge'
    ? styles.checkupStatusChipNudge
    : styles.checkupStatusChipStrong;

  const metricCards = [
    {
      key: 'income',
      kicker: 'Income',
      title: 'Current Monthly Income',
      metric: formatCurrency(runway.currentMonthlyIncome),
      description: 'Estimated monthly net income during your career shift.',
    },
    {
      key: 'expenses',
      kicker: 'Expenses',
      title: 'Current Monthly Expenses',
      metric: formatCurrency(runway.currentMonthlyExpenses),
      description: 'Living costs used to model your short-term runway.',
    },
    {
      key: 'gap',
      kicker: 'Cash Flow',
      title: 'Monthly Gap / Surplus',
      metric: formatMonthlyGapDisplay(runway.monthlyGap),
      description: runway.monthlyGap > 0
        ? 'Expenses currently exceed income during the shift.'
        : 'Income currently covers or exceeds your expenses.',
    },
    {
      key: 'duration',
      kicker: 'Plan',
      title: 'Career Shift Duration',
      metric: `${view.durationMonths} months`,
      description: 'The planned length of your career transition.',
    },
    {
      key: 'runway',
      kicker: 'Runway',
      title: 'Runway',
      metric: view.runwayMetric,
      description: 'How long accessible cash can cover a monthly gap.',
    },
    {
      key: 'liquidity',
      kicker: 'Liquidity',
      title: 'Accessible Short-Term Liquidity',
      metric: formatCurrency(runway.accessibleShortTermLiquidity),
      description: 'Cash you can access quickly with zero to low penalty.',
    },
  ];

  return (
    <div className={styles.checkupDiagnosticPage}>
      <section className={styles.checkupDiagnosticCard} aria-label="Your Financial Runway">
        <header className={styles.checkupDiagnosticHeader}>
          <div className={styles.checkupDiagnosticEyebrowRow}>
            <span className={styles.checkupDiagnosticEyebrow}>
              <span className={styles.checkupDiagnosticEyebrowIconWrap} aria-hidden="true">
                <img
                  src={financialCheckupIcon}
                  alt=""
                  className={styles.checkupDiagnosticEyebrowIcon}
                  decoding="async"
                />
              </span>
              Short-Term Runway
            </span>
            <span className={statusPillClass}>
              <span className={styles.checkupStatusChipDot} aria-hidden="true" />
              Status: {view.statusLabel}
            </span>
            <span className={styles.checkupDiagnosticUpdated}>{formatCheckupUpdatedLabel()}</span>
          </div>
          <h2 className={styles.checkupDiagnosticTitle}>Your Financial Runway</h2>
          <p className={styles.checkupDiagnosticUserLine}>
            <span className={styles.checkupDiagnosticUserIcon} aria-hidden="true">
              <CheckupPersonIcon />
            </span>
            <span>Based on current income, expenses and short-term liquidity.</span>
          </p>
        </header>

        <section className={styles.checkupWhatsHappening} aria-label="What's happening">
          <h3 className={styles.checkupWhatsHappeningTitle}>
            <span className={styles.checkupWhatsHappeningIcon} aria-hidden="true">
              <CheckupSparkleIcon />
            </span>
            What&apos;s Happening
          </h3>
          <div className={styles.checkupSummaryContent}>
            <p>
              This quantifies how much time and flexibility you have for your career shift.
              It&apos;s based on expense modeling and short-term liquidity from sources with zero to low penalty.
            </p>
          </div>
        </section>

        <section className={styles.checkupPillarsSection} aria-label="Short-term runway details">
          <h3 className={styles.checkupPillarsHeading}>Short-Term Runway Details</h3>
          <div className={`${styles.checkupPillarsGrid} ${styles.financialRunwayDetailsGrid}`}>
            {metricCards.map((card) => (
              <article key={card.key} className={styles.checkupPillarCard}>
                <div className={styles.checkupPillarKickerRow}>
                  <span className={styles.checkupPillarKicker}>{card.kicker}</span>
                </div>
                <h4 className={styles.checkupPillarTitle}>{card.title}</h4>
                <p className={styles.checkupPillarMetric}>{card.metric}</p>
                {card.description ? (
                  <p className={styles.checkupPillarDescription}>{card.description}</p>
                ) : null}
              </article>
            ))}
          </div>
        </section>

        <CheckupDiagnosticFooter
          onClose={onBack}
          onStartMove={onStartMove}
          hideExpertCta={isAdmin}
          title="Turn this runway into progress."
          subtitle="Start your next best money move with quick, practical Richuels."
        />
      </section>
    </div>
  );
});

const LONGEVITY_RING_RADIUS = 40;
const LONGEVITY_RING_CIRCUMFERENCE = 2 * Math.PI * LONGEVITY_RING_RADIUS;

function LongevityProgressRing({ percentage, animationValue }) {
  const rawId = useId();
  const gradientId = `longevity-ring-${rawId.replace(/:/g, '')}`;
  const isAtFinal = animationValue === percentage;
  const circleProgress = isAtFinal
    ? (percentage > 80 ? 82 : percentage < 40 ? 39 : percentage)
    : animationValue;
  const offset = LONGEVITY_RING_CIRCUMFERENCE * (1 - Math.min(circleProgress / 100, 1));
  const atZero = animationValue === 0;

  return (
    <>
      <defs>
        <linearGradient id={gradientId} x1="0" y1="1" x2="1" y2="0">
          <stop offset="0%" stopColor="#7EE0D6" />
          <stop offset="36%" stopColor="#1AA39A" />
          <stop offset="70%" stopColor="#0E6A78" />
          <stop offset="100%" stopColor="#16344A" />
        </linearGradient>
      </defs>
      <circle cx="60" cy="60" r={LONGEVITY_RING_RADIUS} fill="none" stroke="#E4EEF2" strokeWidth="15" />
      <circle
        cx="60"
        cy="60"
        r={LONGEVITY_RING_RADIUS}
        fill="none"
        stroke={`url(#${gradientId})`}
        strokeWidth="15"
        strokeDasharray={`${LONGEVITY_RING_CIRCUMFERENCE}`}
        strokeDashoffset={offset}
        className={styles.progressCircle}
        style={{
          strokeDashoffset: offset,
          transition: atZero ? 'stroke-dashoffset 0s' : 'stroke-dashoffset 2.2s cubic-bezier(0.22, 1, 0.36, 1)',
          strokeLinecap: 'round'
        }}
      />
    </>
  );
}

const FinancialDashboard = ({
  onboardingProgress,
  incomeSources = null,
  onboardingAiCheckupAndActionPlan = null,
  onboardingConfetti = null,
  isModal = false,
  showHeader = true,
  showBackButton = false,
  onBack = () => { },
  isAdmin = false,
  useAdminEndpointForSummary = false,
  summaryUsername = null,
  /** Display name for checkup summary header (defaults to localStorage name). */
  displayUserName = null,
  /** Called when all dashboard APIs have finished (checkup plan when applicable). Used by ForecastPage to hide "Loading Forecast...". */
  onAllDataLoaded = null,
  /**
   * Called once runway and longevity display labels are computed.
   * Receives { runwayDisplay: string, longevityDisplay: string }.
   * ForecastPage and AdminOnboardingUsersTable use this to persist labels to v2_onboarding_ai_result.
   */
  onDisplayMetricsReady = null,
  /** Walk Me Through: show the guided tour overlay (user-only, ignored when isAdmin). */
  showWalkMeThrough = false,
  /** Shown while forecast charts and checkup data are still loading. */
  loadingMessage = 'Loading Forecast...',
  /** Called when the user completes the Walk Me Through tour (clicks Finish). */
  onWalkMeThroughDone = null,
  /** Show a "Take a Tour" replay button in the header (user-only). */
  showWalkMeThroughButton = false,
  /** Called when the "Take a Tour" header button is clicked. */
  onWalkMeThroughButtonClick = null,
  /** Optional page intro/hero rendered instead of the default headerBar. */
  headerContent = null,
  /** Optional footer rendered at the bottom of the hub content grid. */
  footerContent = null,
}) => {
  const navigate = useNavigate();

  const resolvedDisplayUserName = displayUserName
    || (typeof window !== 'undefined' ? localStorage.getItem(config.STORAGE_KEYS.NAME) : null)
    || 'User';

  const getLongevitySuccessUi = useCallback((rawScore) => {
    const score = normalizeLongevityPercent(rawScore) ?? 0;
    const cfpDelta = score - CFP_LONGEVITY_BENCHMARK_PCT;
    const delta = cfpDelta > 0
      ? `↑ ${cfpDelta}% above CFP benchmark`
      : cfpDelta === 0
        ? 'At the CFP benchmark'
        : `${Math.abs(cfpDelta)}% below CFP benchmark`;

    if (score >= CFP_LONGEVITY_BENCHMARK_PCT) {
      return {
        rating: 'Excellent',
        emoji: '😊',
        delta,
        deltaTone: 'up',
        description:
          'Your long-term stability gives you the freedom to choose your pace without stress.',
        strokeColor: '#0d3b4e',
      };
    }

    if (score >= 40) {
      return {
        rating: 'On track',
        emoji: '💪',
        delta,
        deltaTone: 'down',
        description:
          'You’re getting close! A few focused tweaks can lift your long-term stability into the CFP safe zone.',
        strokeColor: '#0d3b4e',
      };
    }

    return {
      rating: 'Growing',
      emoji: '🌱',
      delta,
      deltaTone: 'down',
      description:
        'There’s room to grow. Strengthening your plan can give you the long-term stability to choose your pace without stress.',
      strokeColor: '#0d3b4e',
    };
  }, []);

  const [retirementAge, setRetirementAge] = useState(65); // Default fallback
  const [financialData, setFinancialData] = useState(null);
  const [checkupAndActionPlanLocal, setCheckupAndActionPlanLocal] = useState(onboardingAiCheckupAndActionPlan);
  const [animationKey, setAnimationKey] = useState(0);
  const [progressAnimationValue, setProgressAnimationValue] = useState(0);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1025);
  const [chartHeight, setChartHeight] = useState(CHART_HEIGHT_DESKTOP);
  const [chartAxisFontSize, setChartAxisFontSize] = useState(CHART_CONFIG.axis.tickLabel.fontSize);
  const [chartLegendFontSize, setChartLegendFontSize] = useState(CHART_CONFIG.legend.fontSize);
  const [showConfetti, setShowConfetti] = useState(false);
  const [cashFlowFilter, setCashFlowFilter] = useState(CASH_FLOW_FILTER_ALL);
  const [netWorthFilter, setNetWorthFilter] = useState(NET_WORTH_FILTER_ALL);
  const animationTimeoutRef = useRef(null);
  const confettiTimeoutRef = useRef(null);
  /** Raw cash flow data for tooltip actual values when bar chart uses display values (min gap). */
  const cashFlowRawDataRef = useRef(null);
  const cashFlowClipPathId = useId();
  const netWorthClipPathId = useId();
  const transitionCashFlowRows = useMemo(
    () => buildTransitionIncomeCashFlow(onboardingProgress, incomeSources),
    [onboardingProgress, incomeSources],
  );

  // Walk Me Through state and section refs
  const [wmtStep, setWmtStep] = useState(0);
  const [walkMeThroughVisible, setWalkMeThroughVisible] = useState(false);
  const { catalog: contentCatalog } = useContentCatalog();
  walkthroughSteps = orderedWalkthroughSteps(contentCatalog?.walkthrough?.steps);
  const wmtCheckupSummaryRef = useRef(null);
  const wmtCashFlowRef = useRef(null);
  const wmtCareerShiftRef = useRef(null);
  const wmtTransitionRef = useRef(null);
  const wmtLongevitySuccessRef = useRef(null);
  const wmtKeyInsightsRef = useRef(null);
  const wmtLifetimeIncomeRef = useRef(null);
  const wmtProjectedNetWorthRef = useRef(null);
  // Order matches WMT_STEP_ORDER.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const wmtSectionRefs = React.useMemo(
    () => [
      wmtCheckupSummaryRef,
      wmtCashFlowRef,
      wmtCareerShiftRef,
      wmtTransitionRef,
      wmtLongevitySuccessRef,
      wmtKeyInsightsRef,
      wmtLifetimeIncomeRef,
      wmtProjectedNetWorthRef,
    ],
    []
  );

  // Defer Walk Me Through until the content reflection modal is closed (if it was showing)
  useEffect(() => {
    if (!showWalkMeThrough || isAdmin || !financialData) {
      setWalkMeThroughVisible(false);
      return undefined;
    }

    const syncVisibility = () => {
      setWalkMeThroughVisible(!getContentReflectionOpenState());
    };

    syncVisibility();
    return subscribeContentReflectionOpenState(syncVisibility);
  }, [showWalkMeThrough, isAdmin, financialData]);

  // Reset to step 0 every time the tour is actually shown
  useEffect(() => {
    if (walkMeThroughVisible) {
      setWmtStep(0);
    }
  }, [walkMeThroughVisible]);

  const [showCheckupDiagnostic, setShowCheckupDiagnostic] = useState(false);
  const [showRunwayDetails, setShowRunwayDetails] = useState(false);
  const hubRef = useRef(null);
  const isDetailView = showCheckupDiagnostic || showRunwayDetails;

  const openCheckupDiagnostic = useCallback(() => {
    setShowRunwayDetails(false);
    setShowCheckupDiagnostic(true);
  }, []);

  const closeCheckupDiagnostic = useCallback(() => {
    setShowCheckupDiagnostic(false);
  }, []);

  const openRunwayDetails = useCallback(() => {
    setShowCheckupDiagnostic(false);
    setShowRunwayDetails(true);
  }, []);

  const closeRunwayDetails = useCallback(() => {
    setShowRunwayDetails(false);
  }, []);

  const handleStartNextBestMove = useCallback(() => {
    navigate(SEE_ALL_MONEY_MOVES_PATH);
  }, [navigate]);

  useEffect(() => {
    if (walkMeThroughVisible) {
      setShowCheckupDiagnostic(false);
      setShowRunwayDetails(false);
    }
  }, [walkMeThroughVisible]);

  useEffect(() => {
    if (!isDetailView) return;
    hubRef.current?.scrollTo?.({ top: 0, behavior: 'auto' });
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [isDetailView]);

  // Scroll to top when component mounts (only if not in modal)
  useEffect(() => {
    if (!isModal) {
      window.scrollTo(0, 0);
    }
  }, [isModal]);

  // Celebration modal: only close on CONTINUE button; block Escape and click-outside from closing
  useEffect(() => {
    if (!showConfetti || isAdmin) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [showConfetti, isAdmin]);

  // Responsive: window width, chart height, axis/legend font sizes (mobile ≤768px)
  useEffect(() => {
    const updateLayout = () => {
      const w = typeof window !== 'undefined' ? window.innerWidth : MOBILE_BREAKPOINT + 1;
      setWindowWidth(w);
      setChartHeight(getResponsiveChartHeight(w));
      setChartAxisFontSize(getResponsiveAxisFontSize(w));
      setChartLegendFontSize(getResponsiveLegendFontSize(w));
    };

    updateLayout();
    window.addEventListener('resize', updateLayout);
    return () => window.removeEventListener('resize', updateLayout);
  }, []);

  const chartMargins = useMemo(
    () => (windowWidth <= BREAKPOINT_TABLET ? CHART_CONFIG.mobileMargins : CHART_CONFIG.margins),
    [windowWidth]
  );

  const longevityScore = useMemo(
    () => normalizeLongevityPercent(financialData?.retirementSuccess?.percentage),
    [financialData?.retirementSuccess],
  );

  const runwayView = useMemo(
    () => buildFinancialRunwayViewModel(financialData?.careerShiftRunway),
    [financialData?.careerShiftRunway],
  );

  const longevityTourSummary = useMemo(() => {
    const steps = contentCatalog?.walkthrough?.steps;
    const step = (Array.isArray(steps) ? steps : []).find((item) => item?.anchor === 'long_term_success');
    return Array.isArray(step?.summary) ? step.summary : null;
  }, [contentCatalog]);

  const longevityUi = useMemo(
    () => (financialData?.retirementSuccess
      ? getLongevitySuccessUi(longevityScore)
      : null),
    [financialData?.retirementSuccess, getLongevitySuccessUi, longevityScore]
  );

  // Load financial data from onboarding progress
  // Use AbortController so that when React Strict Mode re-runs this effect, the first request is aborted and only one API call completes
  const simulationAbortRef = useRef(null);
  useEffect(() => {
    simulationAbortRef.current = new AbortController();
    const signal = simulationAbortRef.current.signal;

    const loadFinancialData = async () => {
      try {
        // Validate onboardingProgress: must be a non-null object (not array, not null)
        // Note: typeof null === 'object' in JavaScript (quirk), so we need explicit null check
        // Return early silently if null/undefined (expected during loading state from parent). Do not call onAllDataLoaded so parent keeps showing loading until progress is set.
        if (onboardingProgress === null || onboardingProgress === undefined) {
          return;
        }

        if (typeof onboardingProgress !== 'object' || Array.isArray(onboardingProgress)) {
          logger.error('❌ FinancialDashboard: Invalid onboarding progress format', {
            type: typeof onboardingProgress,
            isArray: Array.isArray(onboardingProgress),
            value: onboardingProgress
          });
          onAllDataLoaded?.();
          return;
        }

        const userRetirementAge = parseInt(onboardingProgress["retirement_age"], 10) || 0;
        const dashboardData = await getFinancialDashboardData(onboardingProgress, signal);
        if (signal?.aborted) {
          onAllDataLoaded?.();
          return;
        }
        const currentNetWorth = dashboardData.currentNetWorth;
        const projectedNetWorth = dashboardData.projectedNetWorth;
        const projectedCashFlow = dashboardData.projectedCashFlow;
        const retirementSuccess = dashboardData.retirementSuccess;
        const liquidNetWorthAtRetirement = dashboardData.liquidNetWorthAtRetirement;
        const liquidNetWorthAtDeath = dashboardData.liquidNetWorthAtDeath;

        // Set retirement age state
        if (userRetirementAge && !isNaN(userRetirementAge)) {
          setRetirementAge(userRetirementAge);
        }

        // ============================================================================
        // SECTION: SHORT-TERM RUNWAY CALCULATION
        // ============================================================================
        let careerShiftRunway = null;
        try {
          careerShiftRunway = getCareerShiftRunwayData(onboardingProgress);

          // Check if Runway >= Career Shift Duration to show confetti
          const runwayMonthsNumeric = careerShiftRunway.runwayValue === FINANCIAL_CONSTANTS.FULLY_COVERED
            ? Infinity
            : parseFloat(careerShiftRunway.runwayValue);
          const runwayConditionMet = runwayMonthsNumeric >= careerShiftRunway.careerShiftDurationMonths;

          // Celebration (confetti + congratulations modal) when runway condition is met
          if (isAdmin) {
            setShowConfetti(false);
            if (confettiTimeoutRef.current) {
              clearTimeout(confettiTimeoutRef.current);
              confettiTimeoutRef.current = null;
            }
          } else {
            const shouldShowConfetti = runwayConditionMet && onboardingConfetti === true;

            if (shouldShowConfetti) {
              // Clear any existing confetti timeout
              if (confettiTimeoutRef.current) {
                clearTimeout(confettiTimeoutRef.current);
                confettiTimeoutRef.current = null;
              }

              // Show confetti after values have animated in (wait for metric animations to complete)
              confettiTimeoutRef.current = setTimeout(() => {
                confettiTimeoutRef.current = null;
                setShowConfetti(true);
              }, CONFETTI_DELAY_MS);
            } else if (!runwayConditionMet) {
              // Only hide when runway condition is not met. Modal still closes only when user clicks CONTINUE.
              setShowConfetti(false);
              if (confettiTimeoutRef.current) {
                clearTimeout(confettiTimeoutRef.current);
                confettiTimeoutRef.current = null;
              }
            }
          }
        } catch (error) {
          logger.error('❌ Error calculating Short-Term Runway:', error);
          careerShiftRunway = DEFAULT_CAREER_SHIFT_RUNWAY;
          setShowConfetti(false);
          if (confettiTimeoutRef.current) {
            clearTimeout(confettiTimeoutRef.current);
            confettiTimeoutRef.current = null;
          }
        }

        const finalCareerShiftRunway = careerShiftRunway || DEFAULT_CAREER_SHIFT_RUNWAY;

        // Always set full financial data so cards and checkup generation have currentNetWorth, careerShiftRunway, etc.
        // Chart arrays (projectionData / cashFlow data) may be empty; summary and cards still need the numeric values.
        const fullData = {
          currentNetWorth,
          projectedNetWorth,
          retirementSuccess: retirementSuccess || null,
          projectedCashFlow,
          careerShiftRunway: finalCareerShiftRunway,
          liquidNetWorthAtRetirement,
          liquidNetWorthAtDeath
        };

        if (onboardingAiCheckupAndActionPlan == null) {
          const generatedPlan = await runCheckupPlanIfNeeded({
            fullData,
            onboardingProgress,
            useAdminEndpointForSummary,
            summaryUsername,
            signal
          });
          if (!signal?.aborted && generatedPlan) setCheckupAndActionPlanLocal(generatedPlan);
        }

        await waitUntilPaintReady();
        if (signal?.aborted) {
          onAllDataLoaded?.();
          return;
        }

        // Show forecast content only after checkup plan, fonts, and layout are ready
        setFinancialData(fullData);
        setAnimationKey(prev => prev + 1);

        // Emit computed display labels so the caller can persist them.
        if (onDisplayMetricsReady) {
          const runwayDisp = finalCareerShiftRunway.runwayDisplayValue === 'Fully Covered'
            ? 'Fully Covered'
            : (finalCareerShiftRunway.runwayDisplayValue ?? `${finalCareerShiftRunway.runwayValue} months`);
          const longevityDisp = formatLongevitySuccessScoreForDisplay(retirementSuccess?.percentage ?? null);
          onDisplayMetricsReady({ runwayDisplay: runwayDisp, longevityDisplay: longevityDisp });
        }

        onAllDataLoaded?.();
      } catch (error) {
        if (signal?.aborted) return;
        logger.error('Error loading financial data:', error);
        onAllDataLoaded?.();
      }
    };

    loadFinancialData().catch((error) => {
      if (error?.name !== 'AbortError') logger.error('Error in loadFinancialData:', error);
    });

    // Cleanup: abort in-flight simulation request and clear any pending animations
    return () => {
      simulationAbortRef.current?.abort();
      // Clear any pending animations
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
        animationTimeoutRef.current = null;
      }
      if (confettiTimeoutRef.current) {
        clearTimeout(confettiTimeoutRef.current);
        confettiTimeoutRef.current = null;
      }
    };
    // Only re-run when onboardingProgress changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboardingProgress]);

  // Keep local plan in sync when parent provides it (initial load / admin modal refresh).
  useEffect(() => {
    setCheckupAndActionPlanLocal(onboardingAiCheckupAndActionPlan);
  }, [onboardingAiCheckupAndActionPlan]);

  // Animate the longevity ring from 0 to the calculated score without resetting on remount.
  useEffect(() => {
    const score = normalizeLongevityPercent(financialData?.retirementSuccess?.percentage);
    setProgressAnimationValue(score ?? 0);
  }, [financialData?.retirementSuccess]);

  const checkupSummaryProps = {
    onboardingProgress,
    financialData,
    checkupAndActionPlan: checkupAndActionPlanLocal,
    displayUserName: resolvedDisplayUserName,
    isAdmin,
    onStartMove: handleStartNextBestMove,
  };

  return (
    <div
      ref={hubRef}
      className={`${styles.financialWellnessHub} ${isModal ? styles.modalDashboard : ''}`}
    >
      {showCheckupDiagnostic && financialData ? (
        <CheckupSummarySection
          {...checkupSummaryProps}
          view="diagnostic"
          onBack={closeCheckupDiagnostic}
        />
      ) : null}
      {showRunwayDetails && financialData?.careerShiftRunway ? (
        <FinancialRunwayDetailsPage
          runway={financialData.careerShiftRunway}
          isAdmin={isAdmin}
          onBack={closeRunwayDetails}
          onStartMove={handleStartNextBestMove}
        />
      ) : null}
      {!isDetailView ? headerContent : null}
      {/* Header - Only show if requested */}
      {!isDetailView && !headerContent && showHeader && (
        <div className={dashStyles.headerBar}>
          <div className={dashStyles.headerLeft}>
            {showBackButton && (
              <BackButton className={dashStyles.headerBackButton} onClick={onBack} />
            )}
            <h1 className={dashStyles.headerTitle}>Forecast</h1>
            <div
              className={`${styles.forecastHeaderActions}${showWalkMeThroughButton && !isAdmin ? ` ${styles.forecastHeaderActionsWithTour}` : ''}`}
            >
              <button
                type="button"
                className={styles.aiSummaryButton}
                onClick={() => navigate('/dashboard/library')}
                aria-label="Open library"
              >
                Welcome
              </button>
              {/* Replay control for the legacy Forecast header. The live page uses the shell "Take a Tour" button. */}
              {showWalkMeThroughButton && !isAdmin && (
                <button
                  type="button"
                  className={styles.wmtTourButton}
                  onClick={onWalkMeThroughButtonClick}
                  aria-label="Replay the guided tour"
                >
                  Take a Tour
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {!isDetailView && (!financialData ? (
        <DashboardLoadingState message={loadingMessage} />
      ) : (
        <>
          {/* Main Content Grid - Simple top-to-bottom layout */}
          <div className={styles.contentGrid} id={isModal ? undefined : 'forecast-plan'}>
            <CheckupSummarySection
              {...checkupSummaryProps}
              view="banner"
              wmtRef={wmtCheckupSummaryRef}
              onOpenDiagnostic={openCheckupDiagnostic}
            />
            <CashFlowSnapshotSection
              progress={onboardingProgress}
              runway={financialData?.careerShiftRunway}
              wmtRef={wmtCashFlowRef}
            />
            {/* Top Row Container - Short-Term Runway and Long-Term Success */}
            <div className={styles.bottomRowContainer}>
              {financialData?.careerShiftRunway && (
                <FinancialRunwayCard
                  key={`career-shift-runway-${animationKey}`}
                  runway={financialData.careerShiftRunway}
                  wmtRef={wmtCareerShiftRef}
                  transitionRef={wmtTransitionRef}
                  transitionRows={transitionCashFlowRows}
                  onViewDetails={openRunwayDetails}
                />
              )}

              <div className={styles.longevityColumn}>
                {financialData?.retirementSuccess && longevityUi && (
                  <div
                    ref={wmtLongevitySuccessRef}
                    className={`${styles.card} ${styles.card2} ${styles.longevityCard}`}
                  >
                    <div className={styles.cardHeader}>
                      <div className={styles.cardTitleContainer}>
                        <h3 className={`${styles.sectionTitle} ${styles.longevityTitle}`}>
                          <span>Financial Longevity Score</span>
                          <button
                            type="button"
                            className={styles.longevityInfoBtn}
                            aria-label="About the Financial Longevity Score"
                          >
                            <LongevityInfoIcon />
                            <span role="tooltip" className={styles.longevityInfoTip}>
                              <span className={styles.longevityInfoKicker}>Financial Longevity Score</span>
                              <span className={styles.longevityInfoCopy}>
                                {longevityTourSummary ? (
                                  <ContentParts parts={longevityTourSummary} />
                                ) : (
                                  'Your Longevity Score is your ultimate long-term health check, showing exactly how confident you can be that your money will last your entire life.'
                                )}
                              </span>
                            </span>
                          </button>
                        </h3>
                      </div>
                    </div>

                    <div className={styles.circularProgress}>
                      <svg viewBox="0 0 120 120" className={styles.progressSvg}>
                        <LongevityProgressRing
                          percentage={longevityScore ?? 0}
                          animationValue={progressAnimationValue}
                        />
                      </svg>
                      <div className={styles.card2ScoreBlock}>
                        <span className={styles.card2ScoreValue}>
                          {formatLongevityCardScore(longevityScore)}
                        </span>
                        <span className={styles.longevityRating}>{longevityUi.rating}</span>
                      </div>
                    </div>

                    <p className={`${styles.card2Delta} ${longevityUi.deltaTone === 'down' ? styles.card2DeltaDown : ''}`}>
                      {longevityUi.delta}
                    </p>

                    <p className={styles.longevityNote}>{longevityUi.description}</p>
                  </div>
                )}

                <KeyInsightsCard
                  runway={financialData?.careerShiftRunway}
                  runwayMonthsLabel={runwayView?.headlineNumber}
                  runwayCovered={Boolean(runwayView?.coversPlanned)}
                  cashFlowRows={transitionCashFlowRows}
                  onSeeRecommendations={() => navigate(MEET_EXPERT_PATH)}
                  wmtRef={wmtKeyInsightsRef}
                />
              </div>
            </div>

            {/* 2 & 3. Growth Visualizations — Lifetime Income + Projected Net Worth */}
            <div className={styles.growthChartsSection}>
              {/* 2. Lifetime Income Projection (income by source + expenses) */}
              <div
                ref={wmtLifetimeIncomeRef}
                className={`${styles.card} ${styles.largeCard} ${styles.card4}`}
              >
                <div className={styles.cardHeader}>
                  <ChartCardHeader
                    title="Lifetime Income Projection"
                    subtitle="Your projected annual income through life."
                    filterValue={cashFlowFilter}
                    filterOptions={CASH_FLOW_FILTER_OPTIONS}
                    filterAriaLabel="Filter lifetime income series"
                    onFilterChange={setCashFlowFilter}
                  />
                </div>

                <div className={`${styles.chartScrollWrapper} ${styles.chartRevealLifetime}`}>
                  <div className={styles.chartContainer}>
                    {(() => {
                      const rawData = financialData.projectedCashFlow?.data ?? [];
                      cashFlowRawDataRef.current = rawData;
                      const showAllIncome = cashFlowFilter === CASH_FLOW_FILTER_ALL;
                      const showExpenses = showAllIncome || cashFlowFilter === CASH_FLOW_EXPENSES_KEY;
                      const displayDataset = showAllIncome
                        ? buildCashFlowBarDatasetWithMinGap(rawData).map((row, i) => ({
                          ...row,
                          expenses: Math.max(0, Math.round(Number(rawData[i]?.expenses) || 0))
                        }))
                        : buildCashFlowActualDataset(rawData);
                      const incomeBarSeries = CHART_CONFIG.cashFlowSeries
                        .filter(({ dataKey }) => showAllIncome || cashFlowFilter === dataKey)
                        .map(({ dataKey, label }) => ({
                          type: 'bar',
                          dataKey,
                          label,
                          stack: showAllIncome ? 'income' : undefined,
                          color: CHART_CONFIG.colors[dataKey],
                          valueFormatter: (value, context) => {
                            const raw = cashFlowRawDataRef.current?.[context?.dataIndex];
                            const actual = raw != null
                              ? (
                                dataKey === 'collegeSavingsDrawdown'
                                  ? getCashFlowCollegeSavingsDrawdownTotal(raw)
                                  : (Number(raw[dataKey] ?? (dataKey === 'workIncome' ? raw.income : 0)) || 0)
                              )
                              : value;
                            return formatBarChartValue(actual);
                          }
                        }));
                      const expensesLineSeries = {
                        type: 'line',
                        dataKey: 'expenses',
                        label: 'Expenses & Taxes',
                        color: CHART_CONFIG.colors.expensesLine,
                        showMark: false,
                        curve: 'natural',
                        valueFormatter: (value, context) => {
                          const raw = cashFlowRawDataRef.current?.[context?.dataIndex];
                          const actual = raw != null ? (Number(raw.expenses) || 0) : Number(value) || 0;
                          return formatBarChartValue(actual);
                        }
                      };
                      const combinedSeries = showExpenses
                        ? [...incomeBarSeries, expensesLineSeries]
                        : incomeBarSeries;
                      const combinedColors = combinedSeries.map((series) => series.color);
                      const boundValuesForItem = (item) => {
                        if (showAllIncome) return [sumCashFlowDisplayStack(item), item.expenses || 0];
                        if (cashFlowFilter === CASH_FLOW_EXPENSES_KEY) return [item.expenses || 0];
                        return [item[cashFlowFilter] || 0];
                      };
                      return (
                        <ChartDataProvider
                          key={`cashflow-composed-${animationKey}-${cashFlowFilter}`}
                          dataset={displayDataset}
                          series={combinedSeries}
                          xAxis={[{
                            id: DEFAULT_X_AXIS_KEY,
                            dataKey: 'age',
                            scaleType: 'band',
                            valueFormatter: formatChartAgeAxisValue,
                          }]}
                          yAxis={[{
                            id: DEFAULT_Y_AXIS_KEY,
                            ...calculateChartBounds(displayDataset, boundValuesForItem),
                            min: 0,
                            valueFormatter: formatChartValue
                          }]}
                          height={chartHeight}
                          margin={chartMargins.cashFlowBarChart}
                          colors={combinedColors}
                          animation={CHART_CONFIG.polishedChartAnimation}
                          slotProps={{
                            legend: {
                              direction: 'row',
                              position: { vertical: 'bottom', horizontal: 'middle' },
                              padding: { top: 8, bottom: 2 }
                            }
                          }}
                        >
                          <ChartsWrapper
                            sx={getPolishedDashboardChartSx(chartAxisFontSize, chartLegendFontSize)}
                            legendDirection="row"
                            legendPosition={{ vertical: 'bottom', horizontal: 'middle' }}
                          >
                            <ChartsLegend direction="row" />
                            <ChartsSurface sx={{ width: '100%', maxWidth: '100%' }}>
                              <ChartsGrid vertical={false} horizontal />
                              <g clipPath={`url(#${cashFlowClipPathId})`}>
                                <ChartsAxisHighlight x="band" />
                                <BarPlot borderRadius={4} />
                                <LinePlot />
                              </g>
                              <ChartsAxis />
                              <ChartsClipPath id={cashFlowClipPathId} />
                            </ChartsSurface>
                            <ChartsTooltip
                              trigger="axis"
                              {...getDashboardChartsTooltipProps(chartLegendFontSize)}
                            />
                          </ChartsWrapper>
                        </ChartDataProvider>
                      );
                    })()}
                  </div>
                </div>
              </div>
              {/* 3. Projected Net Worth Card */}
              <div
                ref={wmtProjectedNetWorthRef}
                className={`${styles.card} ${styles.largeCard} ${styles.card3}`}
              >
                <div className={styles.cardHeader}>
                  <ChartCardHeader
                    title="Projected Net Worth"
                    subtitle="Your estimated net worth over time based on current inputs."
                    filterValue={netWorthFilter}
                    filterOptions={NET_WORTH_FILTER_OPTIONS}
                    filterAriaLabel="Filter projected net worth series"
                    onFilterChange={setNetWorthFilter}
                  />
                  <div className={styles.projectionMetrics}>
                    <div className={styles.metric}>
                      <div className={styles.metricValue}>{formatCompactCurrencyTwoDecimals(financialData.currentNetWorth.value)}</div>
                      <div className={styles.metricLabel}>Current Net Worth</div>
                    </div>
                    <div className={styles.metric}>
                      <div className={styles.metricValue}>{formatCompactCurrencyTwoDecimals(financialData.liquidNetWorthAtRetirement)}</div>
                      <div className={styles.metricLabel}>At Retirement ({retirementAge})</div>
                    </div>
                    <div className={styles.metric}>
                      <div className={styles.metricValue}>{formatCompactCurrencyTwoDecimals(financialData.liquidNetWorthAtDeath)}</div>
                      <div className={styles.metricLabel}>At Longevity ({FINANCIAL_CONSTANTS.LIFE_EXPECTANCY})</div>
                    </div>
                  </div>
                </div>

                {/* Stacked Bar Chart — DOM order matches Lifetime Income (header, chart) for tour targeting */}
                <div className={`${styles.chartScrollWrapper} ${styles.chartRevealLifetime}`}>
                  <div className={styles.chartContainer}>
                    {(() => {
                      const netWorthRows = (financialData.projectedNetWorth?.projectionData ?? [])
                        .map(normalizeProjectedNetWorthChartRow);
                      const showAllNetWorth = netWorthFilter === NET_WORTH_FILTER_ALL;
                      const netWorthSeries = CHART_CONFIG.netWorthSeries
                        .filter(({ dataKey }) => showAllNetWorth || netWorthFilter === dataKey)
                        .map(({ dataKey, label, stack }) => ({
                          type: 'bar',
                          dataKey,
                          label,
                          stack: showAllNetWorth ? stack : undefined,
                          color: CHART_CONFIG.colors[dataKey],
                          valueFormatter: (value) => safeFormatCurrency(Number(value) || 0),
                        }));
                      const boundValuesForItem = (item) => {
                        if (showAllNetWorth) {
                          return [
                            item.totalStackedValue,
                            item.liabilities,
                            item.currentDollar,
                            item.futureDollar,
                          ];
                        }
                        return [item[netWorthFilter] || 0];
                      };
                      return (
                        <ChartDataProvider
                          key={`projected-networth-${animationKey}-${netWorthFilter}`}
                          dataset={netWorthRows.map((row) => ({
                            age: row.age,
                            realEstate: row.realEstate,
                            otherAssets: row.otherAssets,
                            liabilities: row.liabilities,
                            currentDollar: row.currentDollar,
                            futureDollar: row.futureDollar,
                          }))}
                          series={netWorthSeries}
                          xAxis={[{
                            id: DEFAULT_X_AXIS_KEY,
                            dataKey: 'age',
                            scaleType: 'band',
                            valueFormatter: formatChartAgeAxisValue,
                          }]}
                          yAxis={[{
                            id: DEFAULT_Y_AXIS_KEY,
                            ...calculateChartBounds(netWorthRows, boundValuesForItem),
                            min: 0,
                            valueFormatter: formatChartValue
                          }]}
                          height={chartHeight}
                          colors={netWorthSeries.map((series) => series.color)}
                          margin={chartMargins.projectedNetWorth}
                          animation={CHART_CONFIG.polishedChartAnimation}
                          slotProps={{
                            legend: {
                              direction: 'row',
                              position: { vertical: 'bottom', horizontal: 'middle' },
                              padding: { top: 8, bottom: 2 }
                            }
                          }}
                        >
                          <ChartsWrapper
                            sx={getPolishedDashboardChartSx(chartAxisFontSize, chartLegendFontSize)}
                            legendDirection="row"
                            legendPosition={{ vertical: 'bottom', horizontal: 'middle' }}
                          >
                            <ChartsLegend direction="row" />
                            <ChartsSurface sx={{ width: '100%', maxWidth: '100%' }}>
                              <ChartsGrid vertical={false} horizontal />
                              <g clipPath={`url(#${netWorthClipPathId})`}>
                                <ChartsAxisHighlight x="band" />
                                <BarPlot borderRadius={4} />
                              </g>
                              <ChartsAxis />
                              <ChartsClipPath id={netWorthClipPathId} />
                            </ChartsSurface>
                            <ChartsTooltip
                              trigger="axis"
                              {...getDashboardChartsTooltipProps(chartLegendFontSize)}
                            />
                          </ChartsWrapper>
                        </ChartDataProvider>
                      );
                    })()}
                  </div>
                </div>
              </div>

            </div>{/* end growthChartsSection */}
            {footerContent}
          </div>

          {/* Confetti Celebration Overlay - Full Screen - Rendered at body level. Only closes when user clicks CONTINUE. */}
          {!isAdmin && showConfetti && typeof document !== 'undefined' && createPortal(
            <div
              className={styles.confettiOverlay}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="congratulations-heading"
            >
              {/* Confetti GIF - Rendered behind modal */}
              <img
                src={`${process.env.PUBLIC_URL || ''}/confetti.gif`}
                alt="Celebration confetti"
                className={styles.confettiImage}
              />
              {/* Congratulations Modal */}
              <div className={styles.congratulationsModal}>
                <div className={styles.modalContent}>
                  {/* Animated Background Gradient */}
                  <div className={styles.modalGradientBg}></div>

                  {/* Party Popper Icon with enhanced animation */}
                  <div className={styles.partyPopperIcon}>
                    <div className={styles.partyPopperEmoji}>🎉</div>
                    {/* Sparkle effects around icon */}
                    <div className={styles.sparkle1}>✨</div>
                    <div className={styles.sparkle2}>✨</div>
                    <div className={styles.sparkle3}>✨</div>
                  </div>

                  {/* Heading with enhanced animation */}
                  <h2 id="congratulations-heading" className={styles.congratulationsHeading}>Congratulations!</h2>

                  {/* Message with enhanced animation */}
                  <p className={styles.congratulationsMessage}>
                    You have sufficient runway for a career shift.
                  </p>

                  {/* Continue Button with enhanced hover effects */}
                  <button
                    className={styles.continueButton}
                    onClick={() => {
                      hideOnboardingConfetti();
                      setShowConfetti(false);

                      if (confettiTimeoutRef.current) {
                        clearTimeout(confettiTimeoutRef.current);
                        confettiTimeoutRef.current = null;
                      }
                    }}
                  >
                    <span className={styles.buttonText}>CONTINUE</span>
                    <span className={styles.buttonShine}></span>
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}
        </>
      ))}

      {/* Walk Me Through Guided Tour Overlay */}
      {walkMeThroughVisible && walkthroughSteps.length > 0 && (
        <WalkMeThroughOverlay
          stepIndex={wmtStep}
          sectionRefs={wmtSectionRefs}
          onNext={() => setWmtStep((s) => Math.min(s + 1, walkthroughSteps.length - 1))}
          onBack={() => setWmtStep((s) => Math.max(s - 1, 0))}
          onFinish={() => {
            if (typeof onWalkMeThroughDone === 'function') onWalkMeThroughDone();
          }}
          onClose={() => {
            if (typeof onWalkMeThroughDone === 'function') onWalkMeThroughDone();
          }}
        />
      )}

    </div>
  );
};

export default FinancialDashboard;